// FoodManager.js - Enhanced food management component with admin capabilities

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, Switch, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Paper, Tabs, Tab,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, Tooltip, Alert, Snackbar,
  Breadcrumbs, Link, Stack, OutlinedInput
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LanguageIcon from '@mui/icons-material/Language';
import SaveIcon from '@mui/icons-material/Save';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import api, { generateFoodImage } from './api';
import { useNavigate } from 'react-router-dom';
import { processImageFile, validateImageFile } from './utils/imageUtils';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from './utils/languages';
import PaginationControls from './components/PaginationControls';
import TagSelector from './components/TagSelector';
import ExportWizard from './components/ExportWizard';
import countriesData from './constants/countries.json';
import { exportFoodData, downloadExportFile } from './utils/exportUtils';
import { usePersistedState, useScrollPosition } from './hooks/usePersistedState';

// Cuisine types available in the system
const CUISINE_TYPES = [
  'AFRICAN',
  'AMERICAN',
  'ARGENTINIAN',
  'ASIAN',
  'BRAZILIAN',
  'BRITISH',
  'CARIBBEAN',
  'CHINESE',
  'EAST_EUROPEAN',
  'ETHIOPIAN',
  'EUROPEAN',
  'FRENCH',
  'FUSION',
  'GERMAN',
  'GLOBAL',
  'GREEK',
  'INDIAN',
  'ITALIAN',
  'JAPANESE',
  'KOREAN',
  'LATIN_AMERICAN',
  'LEBANESE',
  'MEDITERRANEAN',
  'MEXICAN',
  'MIDDLE_EASTERN',
  'MODERN',
  'MOROCCAN',
  'NORTH_KOREAN',
  'PERUVIAN',
  'PERSIAN',
  'POLYNESIA',
  'PORTUGUESE',
  'SCANDINAVIAN',
  'SPANISH',
  'SOUTH_KOREAN',
  'THAI',
  'TURKISH',
  'VIETNAMESE'
];

// Format cuisine type for display
const formatCuisineType = (cuisine) => {
  return cuisine.toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Countries array from the JSON file
// NOVA food-processing groups, shown in the app as the Nova-Score badge
const NOVA_OPTIONS = [
  { value: 1, label: '1 – Unprocessed / minimally processed' },
  { value: 2, label: '2 – Processed culinary ingredient' },
  { value: 3, label: '3 – Processed food' },
  { value: 4, label: '4 – Ultra-processed food' },
];
const NOVA_SOURCE_LABELS = { ai: 'AI suggestion', openfoodfacts: 'OpenFoodFacts', expert: 'expert / set by hand' };
// Same colours as the app's NOVA markers: 1 green, 2 yellow, 3 orange, 4 red
const NOVA_COLORS = { 1: '#22C55E', 2: '#FACC15', 3: '#F97316', 4: '#EF4444' };

const NovaChip = ({ group, source }) => group ? (
  <Tooltip title={`NOVA ${group} – ${NOVA_SOURCE_LABELS[source] || source || 'unknown source'}`}>
    <Chip
      size="small"
      label={`NOVA ${group}`}
      sx={{ bgcolor: NOVA_COLORS[group], color: group === 2 ? '#000' : '#fff', fontWeight: 'bold' }}
    />
  </Tooltip>
) : '-';

// Compact NOVA picker for table rows; '' means not set
const NovaSelect = ({ value, onChange }) => (
  <FormControl size="small" sx={{ minWidth: '100px' }}>
    <Select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      displayEmpty
      renderValue={(v) => (v === '' ? <em>Not set</em> : `NOVA ${v}`)}
    >
      <MenuItem value=""><em>Not set</em></MenuItem>
      {NOVA_OPTIONS.map(option => (
        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

const COUNTRIES = Object.entries(countriesData).map(([code, name]) => ({
  code,
  name
}));

// Memoized Table Row Component to prevent unnecessary re-renders
const FoodTableRow = React.memo(({
  food,
  bulkEditMode,
  singleEditMode,
  currentLanguage,
  generatingImages,
  uploadingImages,
  categories,
  getFoodName,
  getCategoryName,
  getEditedValue,
  getEditedLanguageValue,
  handleFieldChange,
  handleLanguageFieldChange,
  handleImageUpload,
  generateAIData,
  onEdit,
  handleDeleteClick,
  foodTags,
  getTagName,
  handleQuickEdit,
  handleSaveSingleEdit,
  handleCancelSingleEdit,
  savingBulk,
  singleEditTags,
  setSingleEditTags
}) => {
  const displayName = getFoodName(food);
  const categoryName = getCategoryName(food.category);
  
  // Check if this row is in edit mode (either bulk edit or single edit)
  const isInEditMode = bulkEditMode || singleEditMode === food.id;
  const isInSingleEditMode = singleEditMode === food.id;
  

  // Memoized image URL calculation
  const imageUrl = React.useMemo(() => {
    // Check language-specific image first
    const langImageUrl = food.languageSpec?.[currentLanguage]?.customImageUrl;
    if (langImageUrl && (langImageUrl.includes('http') || langImageUrl.includes('data:'))) {
      return langImageUrl;
    }

    // Check main image
    if (food.image && typeof food.image === 'string' && (food.image.includes('http') || food.image.includes('data:'))) {
      return food.image;
    }

    // Return placeholder if no valid image URL found
    return 'https://via.placeholder.com/50x50/e0e0e0/757575?text=No+Image';
  }, [food.image, food.languageSpec, currentLanguage]);

  const hasImage = React.useMemo(() => {
    return (food.image && (food.image.includes('http') || food.image.includes('data:'))) ||
      (food.languageSpec?.[currentLanguage]?.customImageUrl &&
        (food.languageSpec[currentLanguage].customImageUrl.includes('http') ||
          food.languageSpec[currentLanguage].customImageUrl.includes('data:')));
  }, [food.image, food.languageSpec, currentLanguage]);

  // Style objects memoized to prevent re-creation
  const avatarSx = React.useMemo(() => ({
    width: 50,
    height: 50,
    border: hasImage ? '2px solid #e0e0e0' : '2px dashed #bdbdbd',
    '& img': {
      objectFit: 'cover'
    }
  }), [hasImage]);

  const boxSx = React.useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1
  }), []);

  return (
    <TableRow key={food.id} hover>
      {/* Actions Column */}
      <TableCell align="center">
        {isInSingleEditMode ? (
          // Show Save/Cancel buttons when in single edit mode
          <>
            <Tooltip title="Save Changes">
              <IconButton
                size="small"
                onClick={handleSaveSingleEdit}
                color="primary"
                disabled={savingBulk}
              >
                {savingBulk ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton
                size="small"
                onClick={handleCancelSingleEdit}
                color="error"
                disabled={savingBulk}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          // Show regular Edit, Quick Edit, and Delete buttons
          <>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => onEdit(food.id)}
                color="primary"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Quick Edit">
              <IconButton
                size="small"
                onClick={() => handleQuickEdit(food.id)}
                color="secondary"
                sx={{ color: '#ff9800' }}
              >
                <FlashOnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(food.id, displayName)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </TableCell>
      {/* Image Column */}
      <TableCell sx={{ minWidth: isInEditMode ? '250px' : 'auto' }}>
        <Box sx={boxSx}>
          <Avatar
            src={imageUrl}
            alt={displayName}
            sx={avatarSx}
            variant="rounded"
          />
          {!hasImage && !isInEditMode && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              No Image
            </Typography>
          )}
          {isInEditMode && (
            <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: '180px' }}>
              <TextField
                size="small"
                placeholder="Image URL"
                value={getEditedValue(food.id, 'image', food.image) || ''}
                onChange={(e) => handleFieldChange(food.id, 'image', e.target.value)}
                variant="outlined"
                fullWidth
                sx={{ fontSize: '0.75rem' }}
              />
              <Button
                variant="outlined"
                component="label"
                size="small"
                disabled={uploadingImages[food.id]}
                sx={{
                  fontSize: '0.7rem',
                  py: 0.5,
                  minHeight: 'unset'
                }}
              >
                {uploadingImages[food.id] ? (
                  <CircularProgress size={12} />
                ) : (
                  '📁 Upload'
                )}
                <input
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleImageUpload(food.id, file);
                    }
                  }}
                />
              </Button>
            </Box>
          )}
        </Box>
      </TableCell>

      {/* Name Column */}
      <TableCell sx={{ minWidth: isInEditMode ? '200px' : 'auto' }}>
        {isInEditMode ? (
          <TextField
            size="small"
            value={getEditedLanguageValue(food.id, currentLanguage, 'name', displayName)}
            onChange={(e) => handleLanguageFieldChange(food.id, currentLanguage, 'name', e.target.value)}
            variant="outlined"
            fullWidth
            placeholder="Food name"
          />
        ) : (
          <Typography variant="body2" fontWeight="medium">
            {displayName}
          </Typography>
        )}
      </TableCell>

      {/* Category Column */}
      <TableCell sx={{ minWidth: isInEditMode ? '150px' : 'auto' }}>
        {isInEditMode ? (
          <FormControl fullWidth size="small">
            <Select
              value={getEditedValue(food.id, 'category', food.category) || ''}
              onChange={(e) => {
                const selectedCategoryName = e.target.value;
                const selectedCategory = categories.find(cat => cat.name === selectedCategoryName);

                // Update both category name and categoryId
                handleFieldChange(food.id, 'category', selectedCategoryName);
                if (selectedCategory) {
                  handleFieldChange(food.id, 'categoryId', selectedCategory.id);
                }
              }}
              renderValue={(value) => {
                if (!value) return "Select Category";
                return getCategoryName(value);
              }}
              displayEmpty
            >
              <MenuItem value="">Select Category</MenuItem>
              {categories.map(category => (
                <MenuItem key={category.id} value={category.name}>
                  {getCategoryName(category.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Chip
            label={categoryName}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </TableCell>

      {/* Tags Column */}
      <TableCell sx={{ minWidth: isInEditMode ? '200px' : 'auto' }}>
        {isInEditMode ? (
          <Box sx={{ minWidth: '200px' }}>
            <TagSelector
              selectedTagIds={singleEditTags[food.id] || (foodTags[food.id] || []).map(tag => tag.id)}
              onTagsChange={(tagIds) => {
                setSingleEditTags(prev => ({
                  ...prev,
                  [food.id]: tagIds
                }));
              }}
              currentLanguage={currentLanguage}
            />
          </Box>
        ) : (
          foodTags[food.id] && foodTags[food.id].length > 0 ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {foodTags[food.id].map((tag) => (
                <Chip
                  key={tag.id}
                  label={`${tag.emoji} ${getTagName(tag)}`}
                  size="small"
                  sx={{
                    backgroundColor: tag.color,
                    color: tag.contrastColor || '#000',
                    '& .MuiChip-label': {
                      fontSize: '0.75rem'
                    }
                  }}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">No tags</Typography>
          )
        )}
      </TableCell>

      {/* Countries Column */}
      <TableCell align="center">
        {isInEditMode ? (
          <FormControl size="small" sx={{ minWidth: '220px' }}>
            <Select
              multiple
              value={getEditedValue(food.id, 'selectedCountries', 
                (food.foodCountries?.map(fc => fc.country?.code) || food.countries?.map(c => c.code) || []).filter(Boolean)
              )}
              onChange={(e) => handleFieldChange(food.id, 'selectedCountries', e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    '& .MuiMenuItem-root': {
                      minHeight: 'auto',
                      px: 1
                    }
                  }
                }
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((countryCode) => {
                    const country = COUNTRIES.find(c => c.code === countryCode);
                    return (
                      <Chip 
                        key={countryCode} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <img
                              src={`/images/country/${countryCode.toLowerCase()}.svg`}
                              alt={country?.name || countryCode}
                              style={{ width: 14, height: 11, borderRadius: '1px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            {country?.name || countryCode}
                          </Box>
                        }
                        size="small" 
                        style={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {COUNTRIES.map(country => {
                const isSelected = getEditedValue(food.id, 'selectedCountries', 
                  (food.foodCountries?.map(fc => fc.country?.code) || food.countries?.map(c => c.code) || []).filter(Boolean)
                ).includes(country.code);
                return (
                  <MenuItem 
                    key={country.code} 
                    value={country.code}
                    sx={{
                      backgroundColor: isSelected ? '#2e7d32 !important' : 'transparent',
                      color: isSelected ? 'white !important' : 'inherit',
                      '&:hover': {
                        backgroundColor: isSelected ? '#1b5e20 !important' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={`/images/country/${country.code.toLowerCase()}.svg`}
                        alt={country.name}
                        style={{ width: 20, height: 15, borderRadius: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      {country.name}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
            {((food.foodCountries?.length > 0) || (food.countries?.length > 0)) ? (
              // Handle both data formats: foodCountries from database and countries from search
              (food.foodCountries?.map((fc, index) => (
                <Chip 
                  key={fc.id || index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <img
                        src={`/images/country/${fc.country?.code?.toLowerCase()}.svg`}
                        alt={fc.country?.name || fc.country?.code}
                        style={{ width: 12, height: 9, borderRadius: '1px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      {fc.country?.name || fc.country?.code}
                    </Box>
                  }
                  size="small"
                  style={{ fontSize: '0.7rem', height: '20px' }}
                />
              )) || food.countries?.map((country, index) => (
                <Chip 
                  key={country.id || index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <img
                        src={`/images/country/${country.code?.toLowerCase()}.svg`}
                        alt={country.name || country.code}
                        style={{ width: 12, height: 9, borderRadius: '1px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      {country.name || country.code}
                    </Box>
                  }
                  size="small"
                  style={{ fontSize: '0.7rem', height: '20px' }}
                />
              )))
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </Box>
        )}
      </TableCell>

      {/* Description Column */}
      <TableCell sx={{ minWidth: isInEditMode ? '200px' : 'auto' }}>
        {isInEditMode ? (
          <TextField
            value={getEditedLanguageValue(food.id, currentLanguage, 'description',
                   food.languageSpec?.[currentLanguage]?.description || food.description) || ''}
            onChange={(e) => handleLanguageFieldChange(food.id, currentLanguage, 'description', e.target.value)}
            size="small"
            multiline
            rows={2}
            fullWidth
            placeholder="Description"
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                padding: '6px 8px'
              }
            }}
          />
        ) : (
          <Tooltip
            title={food.languageSpec?.[currentLanguage]?.description || food.description || ''}
            placement="top"
            arrow
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                maxWidth: '200px'
              }}
            >
              {food.languageSpec?.[currentLanguage]?.description || food.description || '-'}
            </Typography>
          </Tooltip>
        )}
      </TableCell>

      {/* Nutritional Values */}
      <TableCell align="right" sx={{ minWidth: isInEditMode ? '100px' : 'auto' }}>
        {isInEditMode ? (
          <TextField
            size="small"
            type="number"
            value={getEditedValue(food.id, 'calories', food.calories) || ''}
            onChange={(e) => handleFieldChange(food.id, 'calories', e.target.value)}
            sx={{ width: '80px' }}
            variant="outlined"
            placeholder="Cal"
          />
        ) : (
          food.calories ? Number(food.calories).toFixed(1) : '-'
        )}
      </TableCell>
      <TableCell align="right" sx={{ minWidth: isInEditMode ? '100px' : 'auto' }}>
        {isInEditMode ? (
          <TextField
            size="small"
            type="number"
            value={getEditedValue(food.id, 'protein', food.protein) || ''}
            onChange={(e) => handleFieldChange(food.id, 'protein', e.target.value)}
            sx={{ width: '80px' }}
            variant="outlined"
            placeholder="Prot"
          />
        ) : (
          food.protein ? Number(food.protein).toFixed(1) : '-'
        )}
      </TableCell>
      <TableCell align="right" sx={{ minWidth: isInEditMode ? '100px' : 'auto' }}>
        {isInEditMode ? (
          <TextField
            size="small"
            type="number"
            value={getEditedValue(food.id, 'fat', food.fat) || ''}
            onChange={(e) => handleFieldChange(food.id, 'fat', e.target.value)}
            sx={{ width: '80px' }}
            variant="outlined"
            placeholder="Fat"
          />
        ) : (
          food.fat ? Number(food.fat).toFixed(1) : '-'
        )}
      </TableCell>
      <TableCell align="right" sx={{ minWidth: isInEditMode ? '100px' : 'auto' }}>
        {isInEditMode ? (
          <TextField
            size="small"
            type="number"
            value={getEditedValue(food.id, 'carbs', food.carbs) || ''}
            onChange={(e) => handleFieldChange(food.id, 'carbs', e.target.value)}
            sx={{ width: '80px' }}
            variant="outlined"
            placeholder="Carbs"
          />
        ) : (
          food.carbs ? Number(food.carbs).toFixed(1) : '-'
        )}
      </TableCell>
      <TableCell align="right" sx={{ minWidth: isInEditMode ? '100px' : 'auto' }}>
        {isInEditMode ? (
          <TextField
            size="small"
            type="number"
            value={getEditedValue(food.id, 'glycemicIndex', food.glycemicIndex) || ''}
            onChange={(e) => handleFieldChange(food.id, 'glycemicIndex', e.target.value)}
            sx={{ width: '80px' }}
            variant="outlined"
            placeholder="GI"
          />
        ) : (
          food.glycemicIndex ? Number(food.glycemicIndex).toFixed(0) : '-'
        )}
      </TableCell>
      <TableCell align="center" sx={{ minWidth: isInEditMode ? '120px' : 'auto' }}>
        {isInEditMode ? (
          <NovaSelect
            value={getEditedValue(food.id, 'novaGroup', food.novaGroup)}
            onChange={(value) => handleFieldChange(food.id, 'novaGroup', value)}
          />
        ) : (
          <NovaChip group={food.novaGroup} source={food.novaSource} />
        )}
      </TableCell>

      {/* Unit Type Column */}
      <TableCell align="center">
        {isInEditMode ? (
          <FormControl size="small" sx={{ minWidth: '100px' }}>
            <Select
              value={getEditedValue(food.id, 'unitType', food.unitType) || 'gram'}
              onChange={(e) => handleFieldChange(food.id, 'unitType', e.target.value)}
              variant="outlined"
            >
              <MenuItem value="gram">gram</MenuItem>
              <MenuItem value="ml">ml</MenuItem>
              <MenuItem value="piece_portion">piece/portion</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2">
            {food.unitType || 'gram'}
          </Typography>
        )}
      </TableCell>

      {/* Unit Value Column */}
      <TableCell align="center">
        {isInEditMode ? (
          <TextField
            size="small"
            type="number"
            placeholder="100"
            value={getEditedValue(food.id, 'unitValue', food.unitValue) || food.unitValue || '100'}
            onChange={(e) => handleFieldChange(food.id, 'unitValue', e.target.value)}
            sx={{ width: '80px' }}
            variant="outlined"
            inputProps={{ min: 0, step: 0.1 }}
          />
        ) : (
          <Typography variant="body2">
            {food.unitValue || 100}
          </Typography>
        )}
      </TableCell>

      {/* Cuisine Type Column */}
      <TableCell align="center">
        {isInEditMode ? (
          <FormControl size="small" sx={{ minWidth: '150px' }}>
            <Select
              value={getEditedValue(food.id, 'cuisineType', food.cuisineType) || ''}
              onChange={(e) => handleFieldChange(food.id, 'cuisineType', e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {CUISINE_TYPES.map(cuisine => (
                <MenuItem key={cuisine} value={cuisine}>
                  {formatCuisineType(cuisine)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2">
            {food.cuisineType ? formatCuisineType(food.cuisineType) : '-'}
          </Typography>
        )}
      </TableCell>

      {/* AI Column (only shown in edit mode) */}
      {isInEditMode && (
        <TableCell align="center">
          <Tooltip title="Generate AI Data">
            <IconButton
              size="small"
              onClick={() => generateAIData(food.id, displayName, food.category)}
              color="secondary"
              disabled={generatingImages[food.id]}
            >
              {generatingImages[food.id] ? (
                <CircularProgress size={20} />
              ) : (
                <AutoAwesomeIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </TableCell>
      )}

      {/* Show Original Column */}
      <TableCell align="center">
        {isInEditMode ? (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={getEditedValue(food.id, 'showOriginalAmount', food.showOriginalAmount) === true || getEditedValue(food.id, 'showOriginalAmount', food.showOriginalAmount) === 'true'}
                onChange={(e) => handleFieldChange(food.id, 'showOriginalAmount', e.target.checked)}
              />
            }
            label=""
            sx={{
              fontSize: '0.7rem',
              '& .MuiFormControlLabel-label': {
                fontSize: '0.7rem'
              }
            }}
          />
        ) : (
          <Typography variant="body2" align="center">
            {(food.showOriginalAmount === true || food.showOriginalAmount === 'true') ? '✅' : '❌'}
          </Typography>
        )}
      </TableCell>

      {/* Created At Column */}
      <TableCell align="center">
        <Typography variant="body2" color="textSecondary">
          {food.createdAt ? new Date(food.createdAt).toLocaleDateString() : 'N/A'}
        </Typography>
      </TableCell>

      {/* Updated At Column */}
      <TableCell align="center">
        <Typography variant="body2" color="textSecondary">
          {food.updatedAt ? new Date(food.updatedAt).toLocaleDateString() : 'N/A'}
        </Typography>
      </TableCell>
    </TableRow>
  );
});

FoodTableRow.displayName = 'FoodTableRow';

// Food List Component
function FoodList({ onEdit, onBack, currentLanguage, setCurrentLanguage }) {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState(AVAILABLE_LANGUAGES);
  
  // Ref to track current search query to avoid stale closures

  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = usePersistedState('foodManager_page', 0);
  const [rowsPerPage, setRowsPerPage] = usePersistedState('foodManager_rowsPerPage', 50);
  const [totalCount, setTotalCount] = useState(0);
  
  // Scroll position management (3 second idle delay before saving)
  const { scrollPosition, saveScrollPosition, restoreScrollPosition, resetScrollPosition } = useScrollPosition('foodManager', 3000);
  const tableContainerRef = useRef(null);
  const restoreScrollRef = useRef(restoreScrollPosition);
  
  // Update the ref when the function changes
  useEffect(() => {
    restoreScrollRef.current = restoreScrollPosition;
  }, [restoreScrollPosition]);
  const [searchQuery, setSearchQuery] = useState('');
  const [imageFilter, setImageFilter] = useState('all'); // 'all', 'with-images', 'without-images'
  const [novaFilter, setNovaFilter] = useState('all'); // 'all', 1-4, 'none'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'createdAt', 'updatedAt'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc', 'desc'
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [singleEditMode, setSingleEditMode] = useState(null); // foodId of the row being edited, or null
  const [singleEditTags, setSingleEditTags] = useState({}); // Track tag changes for single edit mode
  const [editedFoods, setEditedFoods] = useState({});
  const [savingBulk, setSavingBulk] = useState(false);
  const [generatingImages, setGeneratingImages] = useState({});
  const [uploadingImages, setUploadingImages] = useState({});
  const [newRows, setNewRows] = useState([]);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [cacheRefreshDialog, setCacheRefreshDialog] = useState(false);
  const [csvImportDialog, setCsvImportDialog] = useState(false);
  const [languageEditDialog, setLanguageEditDialog] = useState({
    open: false,
    food: null,
    editingLanguages: {}
  });
  const [foodTags, setFoodTags] = useState({});
  const [newRowTags, setNewRowTags] = useState({});
  const [exportDialog, setExportDialog] = useState(false);

  const isInitialMount = useRef(true);
  const lastSearchQuery = useRef(searchQuery);
  const pageRef = useRef(page);
  
  useEffect(() => {
    fetchCategories();
  }, []);

  // Keep pageRef in sync with page state
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Fetch tags for foods

  const fetchFoods = useCallback(async (pageNumber) => {
    // Use the passed pageNumber, or fall back to pageRef.current
    const currentPage = pageNumber !== undefined ? pageNumber : pageRef.current;
    
    setLoading(true);
    try {
      const params = {
        page: currentPage, // API expects 0-based pagination
        number: rowsPerPage, // Always use normal pagination, even in bulk edit mode
        language: currentLanguage,
        sortBy: sortBy,
        sortDirection: sortDirection
      };

      if (categoryFilter && categoryFilter !== 'all') {
        params.category = categoryFilter;
      }

      if (novaFilter !== 'all') {
        params.novaGroups = novaFilter;
      }

      if (searchQuery.trim()) {
        params.query = searchQuery.trim();
      }

      const response = await api.get('/foods/search-for-admin-panel', { params });

      
      if (response.data.success) {
        let filteredFoods = response.data.data.foods;
        
        // Get the total count from the API response (it's totalResults in the actual response)
        const totalCountFromApi = response.data.data.totalResults || response.data.data.totalCount || response.data.data.total || filteredFoods.length;
        

        // Apply image filter
        if (imageFilter === 'with-images') {
          filteredFoods = filteredFoods.filter(food => {
            const hasMainImage = food.image && food.image.trim() !== '';
            const hasLanguageImage = food.languageSpec && Object.values(food.languageSpec).some(lang =>
              lang.customImageUrl && lang.customImageUrl.trim() !== ''
            );
            return hasMainImage || hasLanguageImage;
          });
        } else if (imageFilter === 'without-images') {
          filteredFoods = filteredFoods.filter(food => {
            const hasMainImage = food.image && food.image.trim() !== '';
            const hasLanguageImage = food.languageSpec && Object.values(food.languageSpec).some(lang =>
              lang.customImageUrl && lang.customImageUrl.trim() !== ''
            );
            return !hasMainImage && !hasLanguageImage;
          });
        }

        setFoods(filteredFoods);
        // Use the total count from API, not the filtered length
        setTotalCount(totalCountFromApi);
        
        // Extract tags from the food data (tags are now included in the main response)
        const tagsData = {};
        filteredFoods.forEach(food => {
          if (food.tags && food.tags.length > 0) {
            tagsData[food.id] = food.tags;
          } else {
            tagsData[food.id] = [];
          }
        });
        setFoodTags(tagsData);

        // Extract available languages from food items, always include core languages
        const languages = new Set(AVAILABLE_LANGUAGES);
        response.data.data.foods.forEach(food => {
          if (food.languageSpec) {
            Object.keys(food.languageSpec).forEach(lang => languages.add(lang));
          }
        });
        setAvailableLanguages(Array.from(languages));
      }
    } catch (error) {
      console.error('Failed to fetch foods:', error);
      // Fallback to empty state
      setFoods([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      
      // Restore scroll position after data loads
      setTimeout(() => {
        if (tableContainerRef.current && restoreScrollRef.current) {
          restoreScrollRef.current(tableContainerRef.current);
        }
      }, 100);
    }
  }, [rowsPerPage, currentLanguage, categoryFilter, imageFilter, novaFilter, searchQuery, sortBy, sortDirection]);

  // Single effect to handle all data fetching
  useEffect(() => {
    let timeoutId;
    
    // Handle initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchFoods(page);
      return;
    }
    
    // If search query changed, debounce and reset page
    if (searchQuery !== lastSearchQuery.current) {
      lastSearchQuery.current = searchQuery;
      
      timeoutId = setTimeout(() => {
        setPage(0); // Reset to first page when actually searching
        fetchFoods(0); // Pass 0 as the page number for new searches
      }, 1500);
    } else {
      // For all other changes (page, filters, etc), fetch immediately
      fetchFoods(page);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchFoods, page, rowsPerPage, categoryFilter, currentLanguage, imageFilter, novaFilter, searchQuery, sortBy, sortDirection]);


  const fetchCategories = async () => {
    try {
      const response = await api.get('/category/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleDeleteClick = (id, name) => {
    setDeleteDialog({ open: true, id, name });
  };

  const handleDeleteConfirm = async () => {
    try {
      // Delete from API
      await api.delete(`/foods/${deleteDialog.id}`);

      // Show success notification
      setNotification({
        open: true,
        message: `Food "${deleteDialog.name}" deleted successfully!`,
        severity: 'success'
      });

      // Refresh the food list
      fetchFoods();
      setDeleteDialog({ open: false, id: null, name: '' });
    } catch (error) {
      console.error('Failed to delete food:', error);
      setNotification({
        open: true,
        message: 'Failed to delete food. Please try again.',
        severity: 'error'
      });
      setDeleteDialog({ open: false, id: null, name: '' });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
    resetScrollPosition(); // Reset scroll to top when page changes
  }, [resetScrollPosition]);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    resetScrollPosition(); // Reset scroll to top when rows per page changes
  }, [resetScrollPosition]);

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setSearchQuery(value);
    // Don't reset page here - let the debounced effect handle it
  }, []);

  const getCategoryName = useCallback((categoryName) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (category && category.languageSpec && category.languageSpec[currentLanguage]) {
      return category.languageSpec[currentLanguage].name;
    }
    return categoryName;
  }, [categories, currentLanguage]);

  const getFoodName = useCallback((food) => {
    if (food.languageSpec && food.languageSpec[currentLanguage] && food.languageSpec[currentLanguage].name) {
      return food.languageSpec[currentLanguage].name;
    }
    return food.name;
  }, [currentLanguage]);

  const getLanguageDisplayName = useCallback((lang) => {
    return lang.charAt(0).toUpperCase() + lang.slice(1);
  }, []);

  // Helper function to get tag name for current language
  const getTagName = useCallback((tag) => {
    if (tag.languageSpec && tag.languageSpec[currentLanguage] && tag.languageSpec[currentLanguage].name) {
      return tag.languageSpec[currentLanguage].name;
    }
    return tag.name || '';
  }, [currentLanguage]);

  // Handle scroll events for scroll position persistence
  const handleScroll = useCallback((e) => {
    saveScrollPosition(e.target);
  }, [saveScrollPosition]);

  // Handle sorting
  const handleSort = useCallback((field) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortBy(field);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting
  }, [sortBy, sortDirection]);

  // Sortable Table Header Component
  const SortableTableCell = ({ children, field, align = 'left' }) => {
    const isActive = sortBy === field;
    const isAsc = sortDirection === 'asc';
    
    return (
      <TableCell 
        align={align}
        sx={{ 
          fontWeight: 'bold', 
          backgroundColor: 'primary.light', 
          color: 'white',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'primary.main'
          }
        }}
        onClick={() => handleSort(field)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {children}
          {isActive && (
            isAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
          )}
        </Box>
      </TableCell>
    );
  };


  // New row and CSV import functions
  const addNewRow = () => {
    const newRowId = `new_${Date.now()}`;
    const newRow = {
      id: newRowId,
      name: '',
      category: '',
      categoryId: null, // Initialize as null
      calories: '',
      protein: '',
      fat: '',
      carbs: '',
      glycemicIndex: '',
      novaGroup: '',
      image: '',
      unitType: 'gram', // Default unit type
      unitValue: '100', // Default unit value
      cuisineType: '', // Initialize cuisine type
      selectedCountries: [], // Initialize countries array
      showOriginalAmount: false, // Initialize show original amount
      isNew: true,
      languageSpec: {
        [currentLanguage]: { name: '', customImageUrl: '' }
      }
    };
    setNewRows(prev => [...prev, newRow]);
    setEditedFoods(prev => ({
      ...prev,
      [newRowId]: newRow
    }));
  };

  const removeNewRow = (rowId) => {
    setNewRows(prev => prev.filter(row => row.id !== rowId));
    setEditedFoods(prev => {
      const updated = { ...prev };
      delete updated[rowId];
      return updated;
    });
    setNewRowTags(prev => {
      const updated = { ...prev };
      delete updated[rowId];
      return updated;
    });
  };

  const handleCsvImport = () => {
    setCsvImportDialog(true);
  };

  const handleExportData = async (exportConfig) => {
    try {
      const result = await exportFoodData(exportConfig);
      
      if (result.success && result.data.downloadUrl) {
        // Show success notification and download the file
        setNotification({
          open: true,
          message: `Export completed successfully! ${result.data.rowCount} rows exported.`,
          severity: 'success'
        });
        
        // Trigger download
        await downloadExportFile(result.data.exportId, result.data.filename);
      }
    } catch (error) {
      console.error('Export failed:', error);
      setNotification({
        open: true,
        message: error.message || 'Export failed. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCacheRefresh = () => {
    if (refreshingCache) return;
    setCacheRefreshDialog(true);
  };

  const confirmCacheRefresh = async () => {
    setCacheRefreshDialog(false);
    setRefreshingCache(true);
    try {
      await api.refreshCache();
      setNotification({
        open: true,
        message: 'Cache refreshed successfully! All data has been updated.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error refreshing cache:', error);
      setNotification({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to refresh cache',
        severity: 'error'
      });
    } finally {
      setRefreshingCache(false);
    }
  };

  const processCsvFile = async (file) => {
    if (!file) return;

    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());

    // Skip header line if it exists
    const dataLines = lines[0].toLowerCase().includes('name') ? lines.slice(1) : lines;

    const newCsvRows = dataLines.map((line, index) => {
      const parts = line.split(',').map(part => part.trim());
      const rowId = `csv_${Date.now()}_${index}`;

      return {
        id: rowId,
        name: parts[0] || '',
        category: parts[1] || '',
        calories: parts[2] || '',
        protein: parts[3] || '',
        fat: parts[4] || '',
        carbs: parts[5] || '',
        glycemicIndex: parts[6] || '',
        image: '',
        unitType: 'gram', // Default unit type
        unitValue: '100', // Default unit value
        isNew: true,
        languageSpec: {
          [currentLanguage]: { name: parts[0] || '', customImageUrl: '' }
        }
      };
    });

    setNewRows(prev => [...prev, ...newCsvRows]);
    setEditedFoods(prev => {
      const updated = { ...prev };
      newCsvRows.forEach(row => {
        updated[row.id] = row;
      });
      return updated;
    });

    setCsvImportDialog(false);
    setNotification({
      open: true,
      message: `Imported ${newCsvRows.length} rows from CSV`,
      severity: 'success'
    });
  };

  const handleFieldChange = useCallback((foodId, field, value) => {
    setEditedFoods(prev => {
      const currentFood = foods.find(f => f.id === foodId);
      
      // If changing unitType, automatically set appropriate unitValue
      if (field === 'unitType') {
        let newUnitValue;
        if (value === 'piece_portion') {
          newUnitValue = 1;
        } else if (value === 'gram' || value === 'ml') {
          newUnitValue = 100;
        }
        
        return {
          ...prev,
          [foodId]: {
            ...currentFood,
            ...prev[foodId],
            unitType: value,
            unitValue: newUnitValue
          }
        };
      }
      
      return {
        ...prev,
        [foodId]: {
          ...currentFood,
          ...prev[foodId],
          [field]: value
        }
      };
    });
  }, [foods]);

  const handleLanguageFieldChange = useCallback((foodId, language, field, value) => {
    setEditedFoods(prev => {
      const currentFood = foods.find(f => f.id === foodId);
      return {
        ...prev,
        [foodId]: {
          ...currentFood,
          ...prev[foodId],
          languageSpec: {
            ...currentFood?.languageSpec,
            ...prev[foodId]?.languageSpec,
            [language]: {
              ...currentFood?.languageSpec?.[language],
              ...prev[foodId]?.languageSpec?.[language],
              [field]: value
            }
          }
        }
      };
    });
  }, [foods]);

  // Image processing now handled in single API call

  const generateAIData = async (foodId, foodName, category) => {
    setGeneratingImages(prev => ({ ...prev, [foodId]: true }));

    try {
      // Get the current generateImage setting from the checkbox
      const shouldGenerateImage = getEditedValue(foodId, 'generateImage', false);

      // Show initial processing message
      setNotification({
        open: true,
        message: shouldGenerateImage ?
          'Generating nutritional data and processing AI image...' :
          'Generating nutritional data...',
        severity: 'info'
      });

      // Single API call for both nutrition data and image generation
      const response = await api.post('/foods/fill-with-ai', {
        name: foodName,
        category: category,
        generateImage: shouldGenerateImage,
        languages: availableLanguages // Include all available languages for translation
      });

      if (response.data.success) {
        const aiData = response.data.data;

        // Update ONLY the 5 nutritional values we need
        if (aiData.calories !== undefined) handleFieldChange(foodId, 'calories', aiData.calories.toString());
        if (aiData.protein !== undefined) handleFieldChange(foodId, 'protein', aiData.protein.toString());
        if (aiData.fat !== undefined) handleFieldChange(foodId, 'fat', aiData.fat.toString());
        if (aiData.carbs !== undefined) handleFieldChange(foodId, 'carbs', aiData.carbs.toString());
        if (aiData.glycemicIndex !== undefined) handleFieldChange(foodId, 'glycemicIndex', aiData.glycemicIndex.toString());

        // Update language names and descriptions for all returned languages
        if (aiData.languageSpec) {
          Object.entries(aiData.languageSpec).forEach(([language, data]) => {
            if (data.name) {
              handleLanguageFieldChange(foodId, language, 'name', data.name);
            }
            if (data.description) {
              handleLanguageFieldChange(foodId, language, 'description', data.description);
            }
          });
        }

        // Update image if generated (backend has already processed it to GCP)
        if (shouldGenerateImage && aiData.generatedImage) {
          handleFieldChange(foodId, 'image', aiData.generatedImage);
        }

        let message = 'AI successfully filled nutritional data!';
        if (shouldGenerateImage && aiData.generatedImage) {
          message = 'AI filled nutritional data and generated processed image (200x200, <100KB)!';
        }

        setNotification({
          open: true,
          message: message,
          severity: 'success'
        });
      } else {
        throw new Error(
          response.data?.error?.userMessage || 
          response.data?.error?.message || 
          response.data?.error || 
          'Failed to get AI analysis'
        );
      }
    } catch (error) {
      console.error('AI data generation error:', error);
      setNotification({
        open: true,
        message: 'Failed to generate AI data. Please try again.',
        severity: 'error'
      });
    } finally {
      setGeneratingImages(prev => ({ ...prev, [foodId]: false }));
    }
  };


  const closeLanguageEditDialog = () => {
    setLanguageEditDialog({
      open: false,
      food: null,
      editingLanguages: {}
    });
  };

  const handleLanguageEditChange = (language, field, value) => {
    setLanguageEditDialog(prev => ({
      ...prev,
      editingLanguages: {
        ...prev.editingLanguages,
        [language]: {
          ...prev.editingLanguages[language],
          [field]: value
        }
      }
    }));
  };

  const handleLanguageImageUploadInDialog = async (language, file) => {
    if (!file) return;

    const uploadKey = `lang_${language}`;
    setUploadingImages(prev => ({ ...prev, [uploadKey]: true }));

    try {
      // Validate and process image (using same validation as other uploads)
      const formDataObj = new FormData();
      formDataObj.append("image", file);

      const response = await api.post('/upload-image', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.success && response.data.url) {
        // Update the language editing state with the uploaded image URL
        handleLanguageEditChange(language, 'customImageUrl', response.data.url);

        setNotification({
          open: true,
          message: `${getLanguageDisplayName(language)} image uploaded successfully!`,
          severity: 'success'
        });
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading language image:', error);
      setNotification({
        open: true,
        message: `Failed to upload ${language} image. Please try again.`,
        severity: 'error'
      });
    } finally {
      setUploadingImages(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const saveLanguageChanges = async () => {
    if (!languageEditDialog.food) return;

    try {
      const updateData = {
        languageSpec: languageEditDialog.editingLanguages
      };

      const response = await api.put(`/foods/${languageEditDialog.food.id}`, updateData);
      
      // Check if the response indicates an error
      if (response.data && response.data.success === false) {
        throw new Error(
          response.data.error?.userMessage || 
          response.data.error?.message || 
          response.data.error || 
          'Failed to update language specifications'
        );
      }

      setNotification({
        open: true,
        message: 'Language specifications updated successfully!',
        severity: 'success'
      });

      // Refresh the food list
      fetchFoods();
      closeLanguageEditDialog();
    } catch (error) {
      console.error('Error updating language specs:', error);
      
      // Extract error message from backend
      let errorMessage = 'Failed to update language specifications';
      if (error.response?.data?.error?.userMessage) {
        errorMessage = error.response.data.error.userMessage;
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (typeof error.response?.data?.error === 'string') {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.userMessage) {
        errorMessage = error.response.data.userMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const saveBulkChanges = async () => {
    // Validate new rows before saving
    const invalidNewRows = [];
    Object.entries(editedFoods).forEach(([foodId, changes]) => {
      if (changes.isNew) {
        const foodName = changes.languageSpec?.[currentLanguage]?.name || changes.name || '';
        const categoryName = changes.category || '';
        
        if (!foodName.trim() || !categoryName.trim()) {
          invalidNewRows.push(foodId);
        }
      }
    });

    if (invalidNewRows.length > 0) {
      setNotification({
        open: true,
        message: `Please fill in Name and Category for all new rows before saving. ${invalidNewRows.length} row(s) missing required fields.`,
        severity: 'error'
      });
      return;
    }

    setSavingBulk(true);
    let successCount = 0;
    let errorCount = 0;
    let createdCount = 0;

    try {

      const promises = Object.entries(editedFoods).map(async ([foodId, changes]) => {
        try {
          if (changes.isNew) {
            // Create new food - transform data to match API expectations
            const { isNew, isAIRow, ...rawData } = changes;


            // Get the food name from language spec or main name field
            const foodName = rawData.languageSpec?.[currentLanguage]?.name || rawData.name || '';

            // Find category by name and get both name and ID
            let categoryName = rawData.category;
            let categoryId = rawData.categoryId || null; // Use the stored categoryId first


            if (rawData.category && !categoryId) {
              // If we have category name but no ID, find it
              const selectedCategory = categories.find(cat => cat.name === rawData.category);
              if (selectedCategory) {
                categoryName = selectedCategory.name;
                categoryId = selectedCategory.id;
              } else {
                console.warn(`Category not found: ${rawData.category}`);
              }
            }

            // If we have a direct categoryId, validate it
            if (categoryId) {
              const categoryById = categories.find(cat => cat.id === parseInt(categoryId));
              if (categoryById) {
                categoryName = categoryById.name;
                categoryId = parseInt(categoryId);
              } else {
                console.warn(`Invalid categoryId: ${categoryId}`);
                categoryId = null;
              }
            }

            // Transform the data structure to match API expectations
            const apiData = {
              name: foodName,
              description: rawData.description || '',
              category: categoryName,
              categoryId: categoryId,
              calories: rawData.calories ? parseFloat(rawData.calories) : null,
              protein: rawData.protein ? parseFloat(rawData.protein) : null,
              fat: rawData.fat ? parseFloat(rawData.fat) : null,
              carbs: rawData.carbs ? parseFloat(rawData.carbs) : null,
              glycemicIndex: rawData.glycemicIndex ? parseFloat(rawData.glycemicIndex) : null,
              novaGroup: rawData.novaGroup ? parseInt(rawData.novaGroup) : null,
              image: rawData.image || '',
              generateImage: rawData.generateImage || false,
              unitType: rawData.unitType || 'g',
              unitValue: rawData.unitValue ? parseFloat(rawData.unitValue) : 100,
              cuisineType: rawData.cuisineType || null,
              countries: rawData.selectedCountries || rawData.countries || [],
              languageSpec: rawData.languageSpec || {}
            };


            const response = await api.post('/foods', apiData);
            
            // Check if the response indicates an error
            if (response.data && response.data.success === false) {
              throw new Error(
                response.data.error?.userMessage || 
                response.data.error?.message || 
                response.data.error || 
                'Failed to create food'
              );
            }
            
            // Save tags if any were selected for this new row
            if (newRowTags[foodId] && newRowTags[foodId].length > 0) {
              try {
                await api.post(`/tags/food/${response.data.data.id}`, { tagIds: newRowTags[foodId] });
              } catch (tagError) {
                console.error('Error saving tags:', tagError);
              }
            }
            
            createdCount++;
          } else {
            // Update existing food
            
            // Transform selectedCountries to countries for API
            const updateData = { ...changes };
            if (updateData.selectedCountries) {
              updateData.countries = updateData.selectedCountries;
              delete updateData.selectedCountries;
            }
            
            const updateResponse = await api.put(`/foods/${foodId}`, updateData);
            
            // Check if the response indicates an error
            if (updateResponse.data && updateResponse.data.success === false) {
              throw new Error(
                updateResponse.data.error?.userMessage || 
                updateResponse.data.error?.message || 
                updateResponse.data.error || 
                'Failed to update food'
              );
            }
            
            successCount++;
          }
        } catch (error) {
          console.error(`Failed to save food ${foodId}:`, error);
          console.error('Error details:', error.response?.data);
          errorCount++;
          
          // Extract error message from backend
          let errorMessage = 'Unknown error';
          if (error.response?.data?.error?.userMessage) {
            errorMessage = error.response.data.error.userMessage;
          } else if (error.response?.data?.error?.message) {
            errorMessage = error.response.data.error.message;
          } else if (typeof error.response?.data?.error === 'string') {
            errorMessage = error.response.data.error;
          } else if (error.response?.data?.userMessage) {
            errorMessage = error.response.data.userMessage;
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          // Show specific error for this food item
          const foodName = changes.languageSpec?.[currentLanguage]?.name || changes.name || foodId;
          setNotification({
            open: true,
            message: `Failed to save "${foodName}": ${errorMessage}`,
            severity: 'error'
          });
        }
      });

      await Promise.all(promises);

      if (successCount > 0 || createdCount > 0) {
        setNotification({
          open: true,
          message: `Successfully ${createdCount > 0 ? `created ${createdCount}` : ''}${createdCount > 0 && successCount > 0 ? ', ' : ''}${successCount > 0 ? `updated ${successCount}` : ''} food items${errorCount > 0 ? `. ${errorCount} failed.` : ''}`,
          severity: errorCount > 0 ? 'warning' : 'success'
        });

        // Clear states first to ensure fresh display
        setEditedFoods({});
        setNewRows([]);
        setNewRowTags({});
        
        // Then refresh the food list
        await fetchFoods();
      }
    } catch (error) {
      console.error('Bulk save error:', error);
      setNotification({
        open: true,
        message: 'Failed to save changes',
        severity: 'error'
      });
    } finally {
      setSavingBulk(false);
    }
  };

  const getEditedValue = useCallback((foodId, field, originalValue) => {
    const editedValue = editedFoods[foodId]?.[field];
    return editedValue !== undefined ? editedValue : originalValue;
  }, [editedFoods]);

  const getEditedLanguageValue = useCallback((foodId, language, field, originalValue) => {
    return editedFoods[foodId]?.languageSpec?.[language]?.[field] !== undefined
      ? editedFoods[foodId].languageSpec[language][field]
      : originalValue;
  }, [editedFoods]);

  // Fetch tags for a single food (used after updating tags in single edit mode)
  const fetchFoodTagsForSingleFood = async (foodId) => {
    try {
      const response = await api.get(`/tags/food/${foodId}`);
      if (response.data && response.data.data) {
        setFoodTags(prev => ({
          ...prev,
          [foodId]: response.data.data
        }));
      }
    } catch (error) {
      console.error('Error fetching tags for food:', error);
    }
  };

  // Single edit mode functions
  const handleQuickEdit = (foodId) => {
    setSingleEditMode(foodId);
    // Initialize tags for this food
    const currentTags = foodTags[foodId] || [];
    setSingleEditTags(prev => ({
      ...prev,
      [foodId]: currentTags.map(tag => tag.id)
    }));
  };

  const handleCancelSingleEdit = () => {
    setSingleEditMode(null);
    // Clear any changes made during single edit
    if (singleEditMode) {
      setEditedFoods(prev => {
        const updated = { ...prev };
        delete updated[singleEditMode];
        return updated;
      });
      setSingleEditTags(prev => {
        const updated = { ...prev };
        delete updated[singleEditMode];
        return updated;
      });
    }
  };

  const handleSaveSingleEdit = async () => {
    if (!singleEditMode) return;
    
    const hasDataChanges = editedFoods[singleEditMode];
    const hasTagChanges = singleEditTags[singleEditMode];
    
    // If no changes at all, just exit
    if (!hasDataChanges && !hasTagChanges) return;

    setSavingBulk(true);
    try {
      // Update food data if there are changes
      if (hasDataChanges) {
        const changes = editedFoods[singleEditMode];
        
        // Transform selectedCountries to countries for API
        const updateData = { ...changes };
        if (updateData.selectedCountries) {
          updateData.countries = updateData.selectedCountries;
          delete updateData.selectedCountries;
        }
        
        const response = await api.put(`/foods/${singleEditMode}`, updateData);
        
        if (response.data && response.data.success === false) {
          throw new Error(
            response.data.error?.userMessage || 
            response.data.error?.message || 
            response.data.error || 
            'Failed to update food'
          );
        }
      }
      
      // Save tags if they were changed
      if (singleEditTags[singleEditMode]) {
        try {
          await api.post(`/tags/food/${singleEditMode}`, { 
            tagIds: singleEditTags[singleEditMode] 
          });
          // Refresh food tags to show updated tags
          await fetchFoodTagsForSingleFood(singleEditMode);
        } catch (tagError) {
          console.error('Error saving tags:', tagError);
          // Don't fail the entire save operation if tags fail
        }
      }
      
      // Clear single edit mode and refresh data
      setSingleEditMode(null);
      setEditedFoods(prev => {
        const updated = { ...prev };
        delete updated[singleEditMode];
        return updated;
      });
      setSingleEditTags(prev => {
        const updated = { ...prev };
        delete updated[singleEditMode];
        return updated;
      });
      
      // Refresh the foods list to show updated values
      await fetchFoods();
      
      setNotification({
        open: true,
        message: 'Food updated successfully',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Error saving single edit:', error);
      setNotification({
        open: true,
        message: `Failed to save changes: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSavingBulk(false);
    }
  };

  const handleImageUpload = async (foodId, file) => {
    if (!file) return;

    setUploadingImages(prev => ({ ...prev, [foodId]: true }));

    try {
      // Validate and process image
      validateImageFile(file);
      const processedFile = await processImageFile(file);

      const formDataObj = new FormData();
      formDataObj.append("image", processedFile);

      const response = await api.post('/upload-image', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.success && response.data.url) {
        handleFieldChange(foodId, 'image', response.data.url);
        setNotification({
          open: true,
          message: `Image uploaded successfully! (${Math.round(processedFile.size / 1024)}KB, 200x200px)`,
          severity: 'success'
        });
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setNotification({
        open: true,
        message: error.message || 'Failed to upload image. Please try again.',
        severity: 'error'
      });
    } finally {
      setUploadingImages(prev => ({ ...prev, [foodId]: false }));
    }
  };

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      px: { xs: 2, sm: 3, md: 4 },
      py: 3
    }}>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4, color: '#64748b' }}>
        <Link
          component="button"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={() => onBack('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link
          component="button"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={() => onBack('/')}
        >
          Food Search
        </Link>
        <Typography color="text.primary">Food Management</Typography>
      </Breadcrumbs>

      {/* Action Header */}
      <Paper elevation={3} sx={{
        p: 3,
        mb: 4,
        backgroundColor: 'white',
        borderRadius: 3,
        border: '1px solid #e2e8f0'
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#1e293b', fontWeight: 'bold', mb: 1 }}>
              🍎 Food Database
            </Typography>
            <Typography variant="body1" color="#64748b">
              Manage and organize your food database efficiently
              {bulkEditMode && (
                <Typography component="span" sx={{
                  ml: 2,
                  px: 2,
                  py: 0.5,
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}>
                  BULK EDIT MODE - Current Page Only ({foods.length} items)
                </Typography>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              onClick={addNewRow}
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: '#10b981',
                '&:hover': { backgroundColor: '#059669' },
                fontWeight: 'bold',
                px: 3
              }}
            >
              Add New Row
            </Button>
            <Button
              onClick={handleCsvImport}
              variant="outlined"
              size="large"
              startIcon={<SaveIcon />}
              sx={{
                borderColor: '#3b82f6',
                color: '#3b82f6',
                '&:hover': {
                  borderColor: '#1d4ed8',
                  backgroundColor: '#eff6ff'
                },
                fontWeight: 'bold',
                px: 3
              }}
            >
              Import CSV
            </Button>
            <Button
              onClick={() => setExportDialog(true)}
              variant="outlined"
              size="large"
              startIcon={<FileDownloadIcon />}
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: '#f0fdf4'
                },
                fontWeight: 'bold',
                px: 3
              }}
            >
              Export Data
            </Button>
            <Button
              onClick={() => onEdit('new')}
              variant="outlined"
              size="large"
              startIcon={<AddIcon />}
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: '#ecfdf5'
                },
                fontWeight: 'bold',
                px: 3
              }}
            >
              Add Food Form
            </Button>
            <Button
              onClick={() => {
                setBulkEditMode(!bulkEditMode);
                if (bulkEditMode) {
                  // Clear editing state when exiting bulk edit mode
                  setEditedFoods({});
                  setNewRows([]);
                  setNewRowTags({});
                  // Refresh data to ensure pagination shows updated values
                  fetchFoods();
                }
              }}
              variant={bulkEditMode ? "contained" : "outlined"}
              size="large"
              startIcon={<EditIcon />}
              disabled={Object.keys(generatingImages).some(key => generatingImages[key])}
              sx={{
                ...(bulkEditMode ? {
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' }
                } : {
                  borderColor: '#f59e0b',
                  color: '#f59e0b',
                  '&:hover': {
                    borderColor: '#d97706',
                    backgroundColor: '#fef3c7'
                  }
                }),
                fontWeight: 'bold',
                px: 3
              }}
            >
              {bulkEditMode ? "Exit Bulk Edit" : "Bulk Edit (Current Page)"}
            </Button>
            <Button
              onClick={handleCacheRefresh}
              variant="outlined"
              size="large"
              startIcon={refreshingCache ? <CircularProgress size={20} /> : <RefreshIcon />}
              disabled={refreshingCache}
              sx={{
                borderColor: '#8b5cf6',
                color: '#8b5cf6',
                '&:hover': {
                  borderColor: '#7c3aed',
                  backgroundColor: '#f3f4f6'
                },
                fontWeight: 'bold',
                px: 3
              }}
            >
              {refreshingCache ? "Refreshing..." : "Refresh Cache"}
            </Button>
          </Box>

          {/* Show save button when there are changes */}
          {(Object.keys(editedFoods).length > 0 || newRows.length > 0) && (
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" color="#64748b">
                {Object.keys(editedFoods).length} items ready to save
              </Typography>
              <Button
                onClick={saveBulkChanges}
                variant="contained"
                size="large"
                startIcon={savingBulk ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={savingBulk || Object.keys(generatingImages).some(key => generatingImages[key])}
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                  fontWeight: 'bold',
                  px: 4
                }}
              >
                {savingBulk ? 'Saving...' : Object.keys(generatingImages).some(key => generatingImages[key]) ? 'AI Processing...' : `Save All Changes`}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          backgroundColor: '#fafafa',
          borderRadius: 2
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
          🎛️ Filters & Language
        </Typography>

        {/* Language Selection Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LanguageIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle2" color="text.primary">
              Select Language:
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {availableLanguages.map(lang => (
              <Chip
                key={lang}
                label={getLanguageDisplayName(lang)}
                onClick={() => {
                  if (lang !== currentLanguage) {
                    setLoading(true); // Show loading immediately
                    setCurrentLanguage(lang);
                  }
                }}
                color={currentLanguage === lang ? 'primary' : 'default'}
                variant={currentLanguage === lang ? 'filled' : 'outlined'}
                size="medium"
                sx={{
                  fontWeight: currentLanguage === lang ? 'bold' : 'normal',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s'
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Filters and Sorting Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Filter by Category"
                  onChange={(e) => {
                    setLoading(true); // Show loading immediately
                    setCategoryFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map(category => (
                    <MenuItem key={category.id} value={category.name}>
                      {getCategoryName(category.name)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Images</InputLabel>
                <Select
                  value={imageFilter}
                  label="Filter by Images"
                  onChange={(e) => {
                    setLoading(true); // Show loading immediately
                    setImageFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All Items</MenuItem>
                  <MenuItem value="with-images">✅ With Images</MenuItem>
                  <MenuItem value="without-images">❌ Without Images</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by NOVA</InputLabel>
                <Select
                  value={novaFilter}
                  label="Filter by NOVA"
                  onChange={(e) => {
                    setLoading(true); // Show loading immediately
                    setNovaFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All NOVA Groups</MenuItem>
                  {NOVA_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={String(option.value)}>NOVA {option.label}</MenuItem>
                  ))}
                  <MenuItem value="none">Not set</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={`${sortBy}-${sortDirection}`}
                  label="Sort By"
                  onChange={(e) => {
                    setLoading(true); // Show loading immediately
                    const [field, direction] = e.target.value.split('-');
                    setSortBy(field);
                    setSortDirection(direction);
                    setPage(0);
                  }}
                >
                  <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                  <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                  <MenuItem value="createdAt-desc">Created At (Newest)</MenuItem>
                  <MenuItem value="createdAt-asc">Created At (Oldest)</MenuItem>
                  <MenuItem value="updatedAt-desc">Updated At (Newest)</MenuItem>
                  <MenuItem value="updatedAt-asc">Updated At (Oldest)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Loading Indicator */}
          {loading && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                px: 2,
                py: 1,
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderRadius: 2,
                border: '1px solid rgba(25, 118, 210, 0.12)',
                minWidth: 'fit-content',
                flexShrink: 0
              }}
            >
              <CircularProgress 
                size={20} 
                thickness={4}
                sx={{ color: 'primary.main' }}
              />
              <Typography 
                variant="body2" 
                color="primary.main"
                fontWeight="medium"
                sx={{ 
                  display: { xs: 'none', sm: 'block' },
                  whiteSpace: 'nowrap'
                }}
              >
                Applying filters...
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Paper sx={{
        mb: 3,
        p: 2,
        backgroundColor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SearchIcon sx={{ color: 'text.secondary' }} />
          <TextField
            size="small"
            placeholder="Search foods by name..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              }
            }}
            variant="outlined"
          />
          {searchQuery && (
            <Typography variant="caption" color="text.secondary">
              Searching in {currentLanguage}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Pagination Controls at Top */}
      {!bulkEditMode && (
        <Paper
          elevation={3}
          sx={{
            borderTop: '1px solid',
            borderTopColor: 'divider',
            borderRadius: '8px 8px 0 0',
            backgroundColor: 'background.paper',
            mb: 0
          }}
        >
          <PaginationControls
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[50, 100, 200, 500]}
            loading={loading}
          />
        </Paper>
      )}

      {/* Table with Sticky Header */}
      <Box
        sx={{
          position: 'relative',
          height: bulkEditMode ? 'calc(100vh - 250px)' : 'calc(100vh - 300px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <TableContainer
          component={Paper}
          elevation={3}
          ref={tableContainerRef}
          onScroll={handleScroll}
          sx={{
            borderRadius: bulkEditMode ? 2 : '0 0 8px 8px',
            flex: 1,
            overflow: 'auto',
            position: 'relative',
            '& .MuiTable-root': {
              minWidth: bulkEditMode ? 1400 : { xs: '100%', sm: 650 }
            },
            // Make table header sticky
            '& .MuiTableHead-root .MuiTableCell-root': {
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: 'primary.light',
              color: 'white',
              fontWeight: 'bold'
            },
            // Add pulse animation keyframes
            '@keyframes pulse': {
              '0%': {
                opacity: 0.4
              },
              '50%': {
                opacity: 0.8
              },
              '100%': {
                opacity: 0.4
              }
            }
          }}
        >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Actions
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Image
              </TableCell>
              <SortableTableCell field="name">
                Name
              </SortableTableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Category
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Tags
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Countries
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Description
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Calories
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Protein (g)
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Fat (g)
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Carbs (g)
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Glycemic Index
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                NOVA
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Unit Type
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Unit Value
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Cuisine Type
              </TableCell>
              {(bulkEditMode || singleEditMode) && (
                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'secondary.light', color: 'white' }}>
                  AI Image
                </TableCell>
              )}
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'white' }}>
                Show Original
              </TableCell>
              <SortableTableCell field="createdAt" align="center">
                Created At
              </SortableTableCell>
              <SortableTableCell field="updatedAt" align="center">
                Updated At
              </SortableTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Render new rows first */}
            {newRows.map((food) => {
              const displayName = food.languageSpec?.[currentLanguage]?.name || food.name;

              return (
                <TableRow
                  key={food.id}
                  sx={{
                    backgroundColor: '#fef3c7',
                    border: '2px solid #f59e0b',
                  }}
                >
                  {/* Actions Column */}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Tooltip title="Generate AI Nutrition Data">
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Get current edited values instead of initial empty values
                            const currentName = getEditedLanguageValue(food.id, currentLanguage, 'name', displayName);
                            const currentCategory = getEditedValue(food.id, 'category', food.category);
                            generateAIData(food.id, currentName, currentCategory);
                          }}
                          color="secondary"
                          disabled={generatingImages[food.id]}
                        >
                          {generatingImages[food.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <AutoAwesomeIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove Row">
                        <IconButton
                          size="small"
                          onClick={() => removeNewRow(food.id)}
                          color="error"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  {/* Image Column */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {(() => {
                        const currentImageUrl = getEditedValue(food.id, 'image', food.image);
                        return currentImageUrl ? (
                          <Avatar
                            src={currentImageUrl}
                            sx={{
                              width: 50,
                              height: 50,
                              border: '2px solid #e0e0e0',
                              backgroundColor: '#f5f5f5'
                            }}
                            variant="rounded"
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: 50,
                              height: 50,
                              border: '2px dashed #bdbdbd',
                              backgroundColor: '#f5f5f5'
                            }}
                            variant="rounded"
                          >
                            🆕
                          </Avatar>
                        );
                      })()}
                      <Typography variant="caption" color="#f59e0b" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                        New Row
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Image URL"
                        value={getEditedValue(food.id, 'image', food.image) || ''}
                        onChange={(e) => handleFieldChange(food.id, 'image', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        disabled={uploadingImages[food.id]}
                        sx={{
                          fontSize: '0.7rem',
                          py: 0.5,
                          minHeight: 'unset'
                        }}
                      >
                        {uploadingImages[food.id] ? (
                          <CircularProgress size={12} />
                        ) : (
                          '📁 Upload'
                        )}
                        <input
                          type="file"
                          hidden
                          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleImageUpload(food.id, file);
                            }
                          }}
                        />
                      </Button>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={getEditedValue(food.id, 'generateImage', food.generateImage) || false}
                            onChange={(e) => handleFieldChange(food.id, 'generateImage', e.target.checked)}
                          />
                        }
                        label="🤖 AI Image"
                        sx={{
                          fontSize: '0.7rem',
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.7rem'
                          }
                        }}
                      />
                      
                    </Box>
                  </TableCell>

                  {/* Name Column */}
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Food name"
                      value={getEditedLanguageValue(food.id, currentLanguage, 'name', displayName)}
                      onChange={(e) => handleLanguageFieldChange(food.id, currentLanguage, 'name', e.target.value)}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>

                  {/* Category Column */}
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={getEditedValue(food.id, 'category', food.category) || ''}
                        onChange={(e) => {
                          const selectedCategoryName = e.target.value;
                          const selectedCategory = categories.find(cat => cat.name === selectedCategoryName);

                          console.log('New row category selection:', {
                            foodId: food.id,
                            categoryName: selectedCategoryName,
                            category: selectedCategory
                          });

                          // Update both category name and categoryId for new rows
                          handleFieldChange(food.id, 'category', selectedCategoryName);
                          if (selectedCategory) {
                            handleFieldChange(food.id, 'categoryId', selectedCategory.id);
                            console.log(`New row - Setting categoryId for ${food.id}: ${selectedCategory.id}`);
                          }
                        }}
                        renderValue={(value) => {
                          if (!value) return "Select Category";
                          return getCategoryName(value);
                        }}
                        displayEmpty
                      >
                        <MenuItem value="">Select Category</MenuItem>
                        {categories.map(category => (
                          <MenuItem key={category.id} value={category.name}>
                            {getCategoryName(category.name)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  {/* Tags Column */}
                  <TableCell>
                    <Box sx={{ minWidth: '200px' }}>
                      <TagSelector
                        selectedTagIds={newRowTags[food.id] || []}
                        onTagsChange={(tagIds) => {
                          setNewRowTags(prev => ({
                            ...prev,
                            [food.id]: tagIds
                          }));
                        }}
                        label=""
                        placeholder="Select tags..."
                        currentLanguage={currentLanguage}
                      />
                    </Box>
                  </TableCell>

                  {/* Countries Column */}
                  <TableCell align="center">
                    {(bulkEditMode || food.isNew) ? (
                      <FormControl size="small" sx={{ minWidth: '220px' }}>
                        <InputLabel>Countries</InputLabel>
                        <Select
                          multiple
                          value={getEditedValue(food.id, 'selectedCountries', food.countries) || []}
                          onChange={(e) => handleFieldChange(food.id, 'selectedCountries', e.target.value)}
                          input={<OutlinedInput label="Countries" />}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                '& .MuiMenuItem-root': {
                                  minHeight: 'auto',
                                  px: 1
                                }
                              }
                            }
                          }}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => {
                                const country = COUNTRIES.find(c => c.code === value);
                                return (
                                  <Chip
                                    key={value}
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <img
                                          src={`/images/country/${value.toLowerCase()}.svg`}
                                          alt={country ? country.name : value}
                                          style={{ width: 14, height: 11, borderRadius: '1px' }}
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        {country ? country.name : value}
                                      </Box>
                                    }
                                    size="small"
                                    sx={{ maxWidth: '120px' }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {COUNTRIES.map((country) => {
                            const isSelected = (getEditedValue(food.id, 'selectedCountries', food.countries) || []).includes(country.code);
                            return (
                              <MenuItem 
                                key={country.code} 
                                value={country.code}
                                sx={{
                                  backgroundColor: isSelected ? '#2e7d32 !important' : 'transparent',
                                  color: isSelected ? 'white !important' : 'inherit',
                                  '&:hover': {
                                    backgroundColor: isSelected ? '#1b5e20 !important' : 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <img
                                    src={`/images/country/${country.code.toLowerCase()}.svg`}
                                    alt={country.name}
                                    style={{ width: 20, height: 15, borderRadius: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  {country.name}
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                        {food.countries?.length > 0 ? (
                          food.countries.map((country, index) => (
                            <Chip 
                              key={country.id || index}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <img
                                    src={`/images/country/${country.code?.toLowerCase()}.svg`}
                                    alt={country.name || country.code}
                                    style={{ width: 12, height: 9, borderRadius: '1px' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  {country.name || country.code}
                                </Box>
                              }
                              size="small"
                              style={{ fontSize: '0.7rem', height: '20px' }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>

                  {/* Description Column */}
                  <TableCell>
                    <TextField
                      size="small"
                      multiline
                      rows={2}
                      placeholder="Description"
                      value={getEditedLanguageValue(food.id, currentLanguage, 'description',
                             food.languageSpec?.[currentLanguage]?.description || food.description) || ''}
                      onChange={(e) => handleLanguageFieldChange(food.id, currentLanguage, 'description', e.target.value)}
                      variant="outlined"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          padding: '6px 8px'
                        }
                      }}
                    />
                  </TableCell>

                  {/* Nutritional Values */}
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Calories"
                      value={getEditedValue(food.id, 'calories', food.calories) || ''}
                      onChange={(e) => handleFieldChange(food.id, 'calories', e.target.value)}
                      sx={{ width: '80px' }}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Protein"
                      value={getEditedValue(food.id, 'protein', food.protein) || ''}
                      onChange={(e) => handleFieldChange(food.id, 'protein', e.target.value)}
                      sx={{ width: '80px' }}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Fat"
                      value={getEditedValue(food.id, 'fat', food.fat) || ''}
                      onChange={(e) => handleFieldChange(food.id, 'fat', e.target.value)}
                      sx={{ width: '80px' }}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Carbs"
                      value={getEditedValue(food.id, 'carbs', food.carbs) || ''}
                      onChange={(e) => handleFieldChange(food.id, 'carbs', e.target.value)}
                      sx={{ width: '80px' }}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      placeholder="GI"
                      value={getEditedValue(food.id, 'glycemicIndex', food.glycemicIndex) || ''}
                      onChange={(e) => handleFieldChange(food.id, 'glycemicIndex', e.target.value)}
                      sx={{ width: '80px' }}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <NovaSelect
                      value={getEditedValue(food.id, 'novaGroup', food.novaGroup)}
                      onChange={(value) => handleFieldChange(food.id, 'novaGroup', value)}
                    />
                  </TableCell>

                  {/* Unit Type Column */}
                  <TableCell align="center">
                    <FormControl size="small" sx={{ minWidth: '100px' }}>
                      <Select
                        value={getEditedValue(food.id, 'unitType', food.unitType) || 'gram'}
                        onChange={(e) => handleFieldChange(food.id, 'unitType', e.target.value)}
                        variant="outlined"
                      >
                        <MenuItem value="gram">gram</MenuItem>
                        <MenuItem value="ml">ml</MenuItem>
                        <MenuItem value="piece_portion">piece/portion</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>

                  {/* Unit Value Column */}
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="number"
                      placeholder="100"
                      value={getEditedValue(food.id, 'unitValue', food.unitValue) || '100'}
                      onChange={(e) => handleFieldChange(food.id, 'unitValue', e.target.value)}
                      sx={{ width: '80px' }}
                      variant="outlined"
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                  </TableCell>

                  {/* Cuisine Type Column */}
                  <TableCell align="center">
                    {(bulkEditMode || food.isNew) ? (
                      <FormControl size="small" sx={{ minWidth: '150px' }}>
                        <Select
                          value={getEditedValue(food.id, 'cuisineType', food.cuisineType) || ''}
                          onChange={(e) => handleFieldChange(food.id, 'cuisineType', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {CUISINE_TYPES.map(cuisine => (
                            <MenuItem key={cuisine} value={cuisine}>
                              {formatCuisineType(cuisine)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body2">
                        {food.cuisineType ? formatCuisineType(food.cuisineType) : '-'}
                      </Typography>
                    )}
                  </TableCell>

                  {/* AI Image Column - Only in Bulk Edit Mode */}
                  {bulkEditMode && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Generate AI image for this food">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => generateAIData(food.id, getEditedLanguageValue(food.id, currentLanguage, 'name', displayName), getEditedValue(food.id, 'category', food.category))}
                              disabled={generatingImages[food.id] || !getEditedValue(food.id, 'name')}
                              color="secondary"
                            >
                              {generatingImages[food.id] ? (
                                <CircularProgress size={20} />
                              ) : (
                                <AutoAwesomeIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          AI Generate
                        </Typography>
                      </Box>
                    </TableCell>
                  )}

                  {/* Show Original Column */}
                  <TableCell align="center">
                    {(bulkEditMode || food.isNew) ? (
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={getEditedValue(food.id, 'showOriginalAmount', food.showOriginalAmount) === true || getEditedValue(food.id, 'showOriginalAmount', food.showOriginalAmount) === 'true'}
                            onChange={(e) => handleFieldChange(food.id, 'showOriginalAmount', e.target.checked)}
                          />
                        }
                        label=""
                        sx={{
                          fontSize: '0.7rem',
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.7rem'
                          }
                        }}
                      />
                    ) : (
                      <Typography variant="body2" align="center">
                        {(getEditedValue(food.id, 'showOriginalAmount', food.showOriginalAmount) === true || getEditedValue(food.id, 'showOriginalAmount', food.showOriginalAmount) === 'true') ? '✅' : '❌'}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Created At Column */}
                  <TableCell align="center">
                    <Typography variant="body2" color="textSecondary">
                      New
                    </Typography>
                  </TableCell>

                  {/* Updated At Column */}
                  <TableCell align="center">
                    <Typography variant="body2" color="textSecondary">
                      -
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Loading State */}
            {loading ? (
              <>
                {/* Loading indicator row */}
                <TableRow>
                  <TableCell colSpan={bulkEditMode ? 15 : 14} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} thickness={4} />
                      <Typography variant="body1" color="text.secondary">
                        Loading food items...
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Please wait while we fetch the latest data
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
                
                {/* Skeleton rows to show table structure */}
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`} sx={{ opacity: 0.3 + (index * 0.1) }}>
                    {/* Actions skeleton */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Box sx={{ width: 24, height: 24, bgcolor: 'grey.300', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <Box sx={{ width: 24, height: 24, bgcolor: 'grey.300', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <Box sx={{ width: 24, height: 24, bgcolor: 'grey.300', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                      </Box>
                    </TableCell>
                    {/* Image skeleton */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            bgcolor: 'grey.300',
                            borderRadius: 2,
                            animation: 'pulse 1.5s ease-in-out infinite'
                          }}
                        />
                      </Box>
                    </TableCell>
                    {/* Name skeleton */}
                    <TableCell>
                      <Box sx={{ height: 20, bgcolor: 'grey.300', borderRadius: 1, animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    {/* Category skeleton */}
                    <TableCell>
                      <Box sx={{ height: 24, width: 80, bgcolor: 'grey.300', borderRadius: 12, animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    {/* Tags skeleton */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box sx={{ height: 20, width: 60, bgcolor: 'grey.300', borderRadius: 10, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <Box sx={{ height: 20, width: 40, bgcolor: 'grey.300', borderRadius: 10, animation: 'pulse 1.5s ease-in-out infinite' }} />
                      </Box>
                    </TableCell>
                    {/* Countries skeleton */}
                    <TableCell align="center">
                      <Box sx={{ height: 20, width: 30, bgcolor: 'grey.300', borderRadius: 1, mx: 'auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    {/* Description skeleton */}
                    <TableCell>
                      <Box sx={{ height: 16, bgcolor: 'grey.300', borderRadius: 1, animation: 'pulse 1.5s ease-in-out infinite' }} />
                      <Box sx={{ height: 16, width: '60%', bgcolor: 'grey.300', borderRadius: 1, mt: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    {/* Nutritional values skeletons */}
                    {Array.from({ length: 5 }).map((_, i) => (
                      <TableCell key={i} align="right">
                        <Box sx={{ height: 16, width: 40, bgcolor: 'grey.300', borderRadius: 1, ml: 'auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
                      </TableCell>
                    ))}
                    {/* Unit Type skeleton */}
                    <TableCell align="center">
                      <Box sx={{ height: 16, width: 50, bgcolor: 'grey.300', borderRadius: 1, mx: 'auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    {/* Unit Value skeleton */}
                    <TableCell align="center">
                      <Box sx={{ height: 16, width: 30, bgcolor: 'grey.300', borderRadius: 1, mx: 'auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    {/* Cuisine skeleton */}
                    <TableCell align="center">
                      <Box sx={{ height: 16, width: 60, bgcolor: 'grey.300', borderRadius: 1, mx: 'auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    {/* AI skeleton (if bulk edit mode) */}
                    {bulkEditMode && (
                      <TableCell align="center">
                        <Box sx={{ width: 24, height: 24, bgcolor: 'grey.300', borderRadius: '50%', mx: 'auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
                      </TableCell>
                    )}
                    {/* Show Original skeleton */}
                    <TableCell align="center">
                      <Box sx={{ height: 16, width: 20, bgcolor: 'grey.300', borderRadius: 1, mx: 'auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    {/* Created/Updated skeletons */}
                    <TableCell align="center">
                      <Box sx={{ height: 16, width: 70, bgcolor: 'grey.300', borderRadius: 1, mx: 'auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ height: 16, width: 70, bgcolor: 'grey.300', borderRadius: 1, mx: 'auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </TableCell>
                    {/* Actions skeleton */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Box sx={{ width: 24, height: 24, bgcolor: 'grey.300', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <Box sx={{ width: 24, height: 24, bgcolor: 'grey.300', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <Box sx={{ width: 24, height: 24, bgcolor: 'grey.300', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : foods.length === 0 && newRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={bulkEditMode ? 15 : 14} align="center" sx={{ py: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                      No food items found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add some foods to get started!
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              foods.map((food) => (
                <FoodTableRow
                  key={`${food.id}-${currentLanguage}`}
                  food={food}
                  bulkEditMode={bulkEditMode}
                  singleEditMode={singleEditMode}
                  currentLanguage={currentLanguage}
                  generatingImages={generatingImages}
                  uploadingImages={uploadingImages}
                  editedFoods={editedFoods}
                  categories={categories}
                  getFoodName={getFoodName}
                  getCategoryName={getCategoryName}
                  getEditedValue={getEditedValue}
                  getEditedLanguageValue={getEditedLanguageValue}
                  handleFieldChange={handleFieldChange}
                  handleLanguageFieldChange={handleLanguageFieldChange}
                  handleImageUpload={handleImageUpload}
                  generateAIData={generateAIData}
                  onEdit={onEdit}
                  handleDeleteClick={handleDeleteClick}
                  foodTags={foodTags}
                  getTagName={getTagName}
                  handleQuickEdit={handleQuickEdit}
                  handleSaveSingleEdit={handleSaveSingleEdit}
                  handleCancelSingleEdit={handleCancelSingleEdit}
                  savingBulk={savingBulk}
                  singleEditTags={singleEditTags}
                  setSingleEditTags={setSingleEditTags}
                />
              ))
            )}
          </TableBody>
        </Table>

      </TableContainer>
      
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Delete Food Item
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>"{deleteDialog.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will permanently remove the food item from the database.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete Food
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog
        open={csvImportDialog}
        onClose={() => setCsvImportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          CSV Import
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV file with food data. Format: name, category, calories, protein, fat, carbs, glycemicIndex
          </Typography>

          <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" fontFamily="monospace">
              Example:<br />
              Apple, Fruits, 52, 0.3, 0.2, 14, 36<br />
              Chicken Breast, Meat, 165, 31, 3.6, 0, 0<br />
              Brown Rice, Grains, 111, 2.6, 0.9, 23, 50
            </Typography>
          </Box>

          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mb: 2, height: '80px', flexDirection: 'column', gap: 1 }}
          >
            📁 Select CSV File
            <Typography variant="caption" color="text.secondary">
              Click to browse and select your CSV file
            </Typography>
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  processCsvFile(file);
                }
              }}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvImportDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Data Dialog */}
      <ExportWizard
        open={exportDialog}
        onClose={() => setExportDialog(false)}
        onExport={handleExportData}
      />

      {/* Cache Refresh Confirmation Dialog */}
      <Dialog
        open={cacheRefreshDialog}
        onClose={() => setCacheRefreshDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RefreshIcon color="primary" />
          Refresh Cache
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to refresh all caches? This will:
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            • Clear all cached food and recipe data<br/>
            • Force reload from the database<br/>
            • Improve data freshness across the system
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This operation may take a few seconds to complete.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCacheRefreshDialog(false)}
            variant="outlined"
            disabled={refreshingCache}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmCacheRefresh}
            variant="contained"
            color="primary"
            disabled={refreshingCache}
            startIcon={refreshingCache ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {refreshingCache ? "Refreshing..." : "Refresh Cache"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Language Edit Dialog */}
      <Dialog
        open={languageEditDialog.open}
        onClose={closeLanguageEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          🌍 Edit Language Specifications
          {languageEditDialog.food && (
            <Typography variant="subtitle2" color="text.secondary">
              Food: {languageEditDialog.food.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {languageEditDialog.food && availableLanguages.map((language) => (
              <Box key={language} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{
                  textTransform: 'capitalize',
                  color: 'primary.main',
                  mb: 2
                }}>
                  {getLanguageDisplayName(language)}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={`Name in ${language}`}
                      value={languageEditDialog.editingLanguages[language]?.name || ''}
                      onChange={(e) => handleLanguageEditChange(language, 'name', e.target.value)}
                      placeholder={`Enter food name in ${language}`}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label={`Description in ${language}`}
                      value={languageEditDialog.editingLanguages[language]?.description || ''}
                      onChange={(e) => handleLanguageEditChange(language, 'description', e.target.value)}
                      placeholder={`Enter food description in ${language}`}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={`Image URL for ${language}`}
                      value={languageEditDialog.editingLanguages[language]?.customImageUrl || ''}
                      onChange={(e) => handleLanguageEditChange(language, 'customImageUrl', e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      fullWidth
                      disabled={uploadingImages[`lang_${language}`]}
                      sx={{
                        height: '36px',
                        fontSize: '0.8rem'
                      }}
                    >
                      {uploadingImages[`lang_${language}`] ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Uploading...
                        </>
                      ) : (
                        <>
                          📁 Upload {getLanguageDisplayName(language)} Image
                        </>
                      )}
                      <input
                        type="file"
                        hidden
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        onChange={(e) => handleLanguageImageUploadInDialog(language, e.target.files[0])}
                      />
                    </Button>
                  </Grid>
                  {languageEditDialog.editingLanguages[language]?.customImageUrl && (
                    <Grid item xs={12}>
                      <Box sx={{
                        mt: 1,
                        p: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        maxWidth: '200px'
                      }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Preview:
                        </Typography>
                        <img
                          src={languageEditDialog.editingLanguages[language].customImageUrl}
                          alt={`${language} preview`}
                          style={{
                            width: '100%',
                            maxHeight: '100px',
                            objectFit: 'contain',
                            borderRadius: '4px'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLanguageEditDialog}>
            Cancel
          </Button>
          <Button
            onClick={saveLanguageChanges}
            variant="contained"
            color="primary"
          >
            Save Language Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
          iconMapping={{
            success: <CheckCircleIcon fontSize="inherit" />,
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Food Form Component
function FoodForm({ foodId, onBack, currentLanguage = DEFAULT_LANGUAGE }) {
  const editMode = foodId !== 'new';
  const [loading, setLoading] = useState(editMode);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [newLanguageDialog, setNewLanguageDialog] = useState(false);
  const [newLanguageName, setNewLanguageName] = useState('');
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [imageGenerating, setImageGenerating] = useState(false);
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [showLongLoadingMessage, setShowLongLoadingMessage] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Default languages - moved to component level for easier future modification
  const defaultLanguages = AVAILABLE_LANGUAGES;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    categoryId: '',
    generateImage: false,
    showOriginalAmount: false,
    calories: '',
    protein: '',
    fat: '',
    carbs: '',
    glycemicIndex: '',
    novaGroup: '',
    image: '',
    unitType: 'g',
    unitValue: '100',
    cuisineType: '',
    selectedCountries: [],
    languageSpec: {
      ...defaultLanguages.reduce((acc, lang) => {
        acc[lang] = { name: '', description: '', customImageUrl: '' };
        return acc;
      }, {})
    },
    imageUploading: false
  });

  const [categories, setCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/category/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [setCategories]);

  const fetchFoodData = useCallback(async () => {
    try {
      const response = await api.get(`/foods/food/${foodId}`);
      if (response.data.success) {
        // Fetch tags for the food
        try {
          const tagResponse = await api.get(`/tags/food/${foodId}`);
          if (tagResponse.data && tagResponse.data.data) {
            setSelectedTagIds(tagResponse.data.data.map(tag => tag.id));
          }
        } catch (tagError) {
          console.error('Error fetching food tags:', tagError);
        }
        const food = response.data.data;
        
        // Extract country codes from foodCountries relationship
        const selectedCountries = food.foodCountries?.map(fc => fc.country?.code).filter(Boolean) || [];
        
        setFormData({
          ...food,
          calories: food.calories || '',
          protein: food.protein || '',
          fat: food.fat || '',
          carbs: food.carbs || '',
          glycemicIndex: food.glycemicIndex || '',
          novaGroup: food.novaGroup || '',
          categoryId: food.categoryId || '',
          unitType: food.unitType || '',
          unitValue: food.unitValue || '',
          cuisineType: food.cuisineType || '',
          showOriginalAmount: food.showOriginalAmount || false,
          selectedCountries: selectedCountries,
          languageSpec: food.languageSpec || {}
        });

        // Set available languages based on food data
        if (food.languageSpec) {
          const existingLanguages = Object.keys(food.languageSpec);

          // Ensure the form has the proper structure for all languages
          const updatedLanguageSpec = { ...food.languageSpec };
          existingLanguages.forEach(lang => {
            if (!updatedLanguageSpec[lang]) {
              updatedLanguageSpec[lang] = { name: '', customImageUrl: '' };
            }
          });

          setFormData(prev => ({
            ...prev,
            languageSpec: updatedLanguageSpec
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch food data:', error);
      alert('Failed to load food data');
      onBack();
    } finally {
      setLoading(false);
    }
  }, [foodId,onBack]);

  useEffect(() => {
    fetchCategories();

    if (editMode) {
      fetchFoodData();
    } else {
      setLoading(false);
    }
  }, [editMode, foodId,fetchCategories,fetchFoodData]);



  // Handle input change for general fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // If changing unitType, automatically set appropriate unitValue
    if (name === 'unitType') {
      let newUnitValue;
      if (value === 'piece_portion') {
        newUnitValue = '1';
      } else if (value === 'gram' || value === 'ml') {
        newUnitValue = '100';
      }

      setFormData({
        ...formData,
        unitType: value,
        unitValue: newUnitValue
      });
    } else if (name === 'name' || name === 'description') {
      // Handle language-specific fields
      if (currentLanguage !== 'english') {
        setFormData({
          ...formData,
          languageSpec: {
            ...formData.languageSpec,
            [currentLanguage]: {
              ...formData.languageSpec[currentLanguage],
              [name]: value
            }
          }
        });
      } else {
        // For English, update both base field and language spec
        setFormData({
          ...formData,
          [name]: value,
          languageSpec: {
            ...formData.languageSpec,
            english: {
              ...formData.languageSpec.english,
              [name]: value
            }
          }
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Handle language specific change
  const handleLanguageChange = (language, field, value) => {
    setFormData({
      ...formData,
      languageSpec: {
        ...formData.languageSpec,
        [language]: {
          ...formData.languageSpec[language],
          [field]: value,
        },
      },
    });

    // If it's the English name and the main name is empty, use it as the main name too
    if (language === "english" && field === "name" && !formData.name) {
      setFormData(prev => ({ ...prev, name: value }));
    }
  };

  // Handle tab change for languages
  const handleTabChange = (_, newValue) => {
    if (newValue === Object.keys(formData.languageSpec).length) {
      // "+" tab was clicked
      setNewLanguageDialog(true);
    } else {
      setCurrentTab(newValue);
    }
  };

  // Handle adding a new language
  const handleAddLanguage = () => {
    if (
      newLanguageName &&
      !Object.keys(formData.languageSpec).includes(
        newLanguageName.toLowerCase()
      )
    ) {
      const language = newLanguageName.toLowerCase();

      setFormData({
        ...formData,
        languageSpec: {
          ...formData.languageSpec,
          [language]: { name: "", customImageUrl: "" },
        },
      });

      setNewLanguageName("");
      setNewLanguageDialog(false);

      // Switch to the new language tab
      setCurrentTab(Object.keys(formData.languageSpec).length);
    }
  };

  // Handle removing a language
  const handleRemoveLanguage = (language) => {
    if (defaultLanguages.includes(language)) {
      showSnackbar(`Cannot remove default language: ${language}`, "error");
      return;
    }

    const updatedLanguageSpec = { ...formData.languageSpec };
    delete updatedLanguageSpec[language];

    setFormData({
      ...formData,
      languageSpec: updatedLanguageSpec,
    });

    // Reset tab if needed
    if (currentTab >= Object.keys(updatedLanguageSpec).length) {
      setCurrentTab(0);
    }
  };

  // Handle image upload for a language (improved)
  const handleLanguageImageUpload = async (language, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Set loading state for this specific image
    setFormData((prev) => ({
      ...prev,
      languageSpec: {
        ...prev.languageSpec,
        [language]: {
          ...prev.languageSpec[language],
          uploading: true,
        },
      },
    }));

    try {
      // Validate and process image
      validateImageFile(file);
      const processedFile = await processImageFile(file);

      const formDataObj = new FormData();
      formDataObj.append("image", processedFile);

      const response = await api.post('/upload-image', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.success && response.data.url) {
        const imageUrl = response.data.url;
        handleLanguageChange(language, "customImageUrl", imageUrl);

        // If this is the first image, use it as the main image
        if (!formData.image) {
          setFormData(prev => ({ ...prev, image: imageUrl }));
        }

        showSnackbar(`${language} image uploaded successfully! (${Math.round(processedFile.size / 1024)}KB, 200x200px)`, "success");
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showSnackbar(error.message || `Failed to upload ${language} image`, "error");
    } finally {
      // Clear loading state
      setFormData((prev) => ({
        ...prev,
        languageSpec: {
          ...prev.languageSpec,
          [language]: {
            ...prev.languageSpec[language],
            uploading: false,
          },
        },
      }));
    }
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  // Close snackbar
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Generate AI image only
  const handleGenerateImage = async () => {
    if (!formData.name.trim()) {
      showSnackbar("Please enter a food name first", "error");
      return;
    }

    setImageGenerating(true);
    try {
      const selectedCategory = formData.categoryId ?
        categories.find(cat => cat.id === parseInt(formData.categoryId)) : null;
      const categoryName = selectedCategory ? selectedCategory.name : null;

      const response = await generateFoodImage(formData.name, categoryName);

      if (response.success) {
        setFormData(prev => ({
          ...prev,
          image: response.data.imageUrl
        }));
        showSnackbar("AI image generated successfully!", "success");
      } else {
        throw new Error(
          response.error?.userMessage || 
          response.error?.message || 
          response.error || 
          "Failed to generate image"
        );
      }
    } catch (error) {
      console.error("Error generating image:", error);
      showSnackbar(error.message || "Failed to generate image", "error");
    } finally {
      setImageGenerating(false);
    }
  };

  // Fill nutritional details with AI (without image)
  const handleFillNutrition = async () => {
    if (!formData.name.trim()) {
      showSnackbar("Please enter a food name first", "error");
      return;
    }

    setNutritionLoading(true);
    setShowLongLoadingMessage(false);

    // Set up timer for long-running request message
    const timer = setTimeout(() => {
      setShowLongLoadingMessage(true);
    }, 10000);

    try {
      const selectedCategory = formData.categoryId ?
        categories.find(cat => cat.id === parseInt(formData.categoryId)) : null;
      const categoryName = selectedCategory ? selectedCategory.name : null;

      const response = await api.post('/foods/fill-with-ai', {
        name: formData.name,
        description: formData.description,
        category: categoryName,
        generateImage: false,
        languages: Object.keys(formData.languageSpec)
      });

      if (response.data.success) {
        const aiData = response.data.data;

        setFormData(prev => ({
          ...prev,
          calories: aiData.calories?.toString() || prev.calories,
          protein: aiData.protein?.toString() || prev.protein,
          fat: aiData.fat?.toString() || prev.fat,
          carbs: aiData.carbs?.toString() || prev.carbs,
          glycemicIndex: aiData.glycemicIndex?.toString() || prev.glycemicIndex,
          languageSpec: {
            ...prev.languageSpec,
            ...aiData.languageSpec
          }
        }));

        showSnackbar("Nutritional details filled successfully with AI!", "success");
      } else {
        throw new Error(
          response.data?.error?.userMessage || 
          response.data?.error?.message || 
          response.data?.error || 
          "Failed to get AI analysis"
        );
      }
    } catch (error) {
      console.error("Error filling nutrition:", error);
      showSnackbar(error.message || "Failed to fill nutrition with AI", "error");
    } finally {
      clearTimeout(timer);
      setNutritionLoading(false);
      setShowLongLoadingMessage(false);
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Food name is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    // Validate numeric fields
    if (formData.calories && (isNaN(formData.calories) || parseFloat(formData.calories) < 0)) {
      newErrors.calories = 'Calories must be a positive number';
    }

    if (formData.protein && (isNaN(formData.protein) || parseFloat(formData.protein) < 0)) {
      newErrors.protein = 'Protein must be a positive number';
    }

    if (formData.fat && (isNaN(formData.fat) || parseFloat(formData.fat) < 0)) {
      newErrors.fat = 'Fat must be a positive number';
    }

    if (formData.carbs && (isNaN(formData.carbs) || parseFloat(formData.carbs) < 0)) {
      newErrors.carbs = 'Carbs must be a positive number';
    }

    if (formData.glycemicIndex && (isNaN(formData.glycemicIndex) || parseFloat(formData.glycemicIndex) < 0 || parseFloat(formData.glycemicIndex) > 100)) {
      newErrors.glycemicIndex = 'Glycemic Index must be between 0 and 100';
    }

    if (formData.unitValue && (isNaN(formData.unitValue) || parseFloat(formData.unitValue) <= 0)) {
      newErrors.unitValue = 'Unit value must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save the food item (override existing handleSubmit)
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      showSnackbar('Please fix the validation errors before saving.', 'error');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...formData,
        calories: formData.calories ? parseFloat(formData.calories) : null,
        protein: formData.protein ? parseFloat(formData.protein) : null,
        fat: formData.fat ? parseFloat(formData.fat) : null,
        carbs: formData.carbs ? parseFloat(formData.carbs) : null,
        glycemicIndex: formData.glycemicIndex ? parseFloat(formData.glycemicIndex) : null,
        novaGroup: formData.novaGroup ? parseInt(formData.novaGroup) : null,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
        unitValue: formData.unitValue ? parseFloat(formData.unitValue) : null,
        cuisineType: formData.cuisineType || null,
        showOriginalAmount: formData.showOriginalAmount || false,
        countries: formData.selectedCountries || []
      };

      let savedFoodId = foodId;
      
      if (editMode) {
        const updateResponse = await api.put(`/foods/${foodId}`, payload);
        // Check if the response indicates an error
        if (updateResponse.data && updateResponse.data.success === false) {
          throw new Error(
            updateResponse.data.error?.userMessage || 
            updateResponse.data.error?.message || 
            updateResponse.data.error || 
            'Failed to update food'
          );
        }
      } else {
        const response = await api.post('/foods', payload);
        
        // Check if the response indicates an error
        if (response.data && response.data.success === false) {
          throw new Error(
            response.data.error?.userMessage || 
            response.data.error?.message || 
            response.data.error || 
            'Failed to create food'
          );
        }
        
        savedFoodId = response.data.data.id;
      }
      
      // Save tags
      if (selectedTagIds.length > 0 || editMode) {
        try {
          await api.post(`/tags/food/${savedFoodId}`, { tagIds: selectedTagIds });
        } catch (tagError) {
          console.error('Error saving tags:', tagError);
          // Don't fail the whole operation if tags fail
        }
      }

      showSnackbar(editMode ? 'Food updated successfully!' : 'Food created successfully!', 'success');

      // Delay navigation to show success message
      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error) {
      console.error('Failed to save food:', error);
      
      // Extract error message from backend
      let errorMessage = 'Failed to save food. Please try again.';
      if (error.response?.data?.error?.userMessage) {
        errorMessage = error.response.data.error.userMessage;
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (typeof error.response?.data?.error === 'string') {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.userMessage) {
        errorMessage = error.response.data.userMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showSnackbar(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Get first letter for avatar placeholder
  const getInitial = () => {
    const displayName = formData.languageSpec?.[currentLanguage]?.name || formData.name;
    return displayName ? displayName.charAt(0).toUpperCase() : "?";
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f8fafc', py: 3 }}>
      <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Back Button & Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" onClick={onBack} sx={{ mr: 2, backgroundColor: 'white', boxShadow: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ color: '#1e293b', fontWeight: 'bold', mb: 0.5 }}>
                {editMode ? 'Edit Food Item' : 'Add New Food Item'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editMode ? 'Update food information and multi-language details' : 'Create a new food entry with complete nutritional information'}
              </Typography>
            </Box>
          </Box>

          {/* Action buttons in header */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={saving || !formData.name || !formData.categoryId}
              size="large"
              sx={{
                height: '48px',
                fontWeight: 'bold',
                px: 4
              }}
            >
              {saving ? "Saving..." : "Save Food Item"}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column - Food Preview */}
          <Grid item xs={12} lg={4}>
            {/* Food Preview Card */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: 'white', borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                📱 Food Preview
              </Typography>

              <Box sx={{ textAlign: 'center', mb: 3 }}>
                {formData.image ? (
                  <Box sx={{
                    width: 150,
                    height: 150,
                    mx: 'auto',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '3px solid #e2e8f0'
                  }}>
                    <img
                      src={formData.image}
                      alt={formData.languageSpec?.[currentLanguage]?.name || formData.name || "Food preview"}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{
                    width: 150,
                    height: 150,
                    mx: 'auto',
                    borderRadius: 3,
                    backgroundColor: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '4rem',
                    fontWeight: 'bold',
                    border: '3px solid #e2e8f0'
                  }}>
                    {getInitial()}
                  </Box>
                )}
              </Box>

              <Typography variant="h5" sx={{
                fontWeight: 'bold',
                textAlign: 'center',
                color: '#1e293b',
                mb: 1
              }}>
{formData.languageSpec?.[currentLanguage]?.name || formData.name || "Food Name"}
              </Typography>

              <Typography variant="body2" sx={{
                textAlign: 'center',
                color: '#64748b',
                mb: 2
              }}>
{(() => {
                  const category = categories.find(cat => cat.id === parseInt(formData.categoryId));
                  if (!category) return "Select Category";
                  return category.languageSpec?.[currentLanguage]?.name || category.name;
                })()}
              </Typography>

              {/* Quick Stats */}
              {(formData.calories || formData.protein || formData.carbs || formData.fat) && (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1,
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f1f5f9',
                  borderRadius: 2
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{formData.calories || "0"}</Typography>
                    <Typography variant="caption" color="text.secondary">Calories</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{formData.protein || "0"}g</Typography>
                    <Typography variant="caption" color="text.secondary">Protein</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{formData.carbs || "0"}g</Typography>
                    <Typography variant="caption" color="text.secondary">Carbs</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary.main">{formData.fat || "0"}g</Typography>
                    <Typography variant="caption" color="text.secondary">Fat</Typography>
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Quick Tips */}
            <Paper elevation={2} sx={{ p: 2, backgroundColor: '#e0f2fe', borderLeft: '4px solid #0277bd' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#0277bd', mb: 1 }}>
                💡 Quick Tips
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                • Use "Fill with AI" to auto-generate nutritional data and translations
                • Upload images for better visual recognition
                • AI automatically translates names to 13+ languages
                • Images are optimized to 200x200px and under 100KB
              </Typography>
            </Paper>
          </Grid>

          {/* Right Column - Form Fields */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* Progress indicator for edit mode */}
              {editMode && (
                <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f3e5f5', border: '1px solid #ba68c8' }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: '#7b1fa2' }}>
                    <EditIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    <strong>Edit Mode:</strong>&nbsp;Updating existing food item "{formData.languageSpec?.[currentLanguage]?.name || formData.name}"
                  </Typography>
                </Paper>
              )}

              {/* Basic Information Section */}
              <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                  📝 Basic Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Food Name"
                      name="name"
                      value={formData.languageSpec?.[currentLanguage]?.name || formData.name}
                      onChange={handleChange}
                      error={!!errors.name}
                      helperText={errors.name || "This will be used as the English name and translated to other languages"}
                      placeholder="e.g., Organic Apple, Grilled Chicken, Brown Rice..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required error={!!errors.category}>
                      <InputLabel>Category</InputLabel>
                      <Select
                        name="category"
                        value={formData.category}
                        label="Category"
                        onChange={(e) => {
                          const selectedCategoryName = e.target.value;
                          const selectedCategory = categories.find(cat => cat.name === selectedCategoryName);

                          setFormData({
                            ...formData,
                            category: selectedCategoryName,
                            categoryId: selectedCategory ? selectedCategory.id : null // Ensure categoryId is set
                          });

                          // Clear error when user selects a category
                          if (errors.category) {
                            setErrors({ ...errors, category: '' });
                          }
                        }}
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          minWidth:"200px"
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a category</em>
                        </MenuItem>
                        {categories.map(category => (
                          <MenuItem key={category.id} value={category.name}>
                            {category.languageSpec?.[currentLanguage]?.name || category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Cuisine Type</InputLabel>
                      <Select
                        name="cuisineType"
                        value={formData.cuisineType}
                        label="Cuisine Type"
                        onChange={handleChange}
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          minWidth: "220px"
                        }}
                      >
                        <MenuItem value="">
                          <em>Select cuisine type</em>
                        </MenuItem>
                        {CUISINE_TYPES.map(cuisine => (
                          <MenuItem key={cuisine} value={cuisine}>
                            {formatCuisineType(cuisine)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Countries</InputLabel>
                      <Select
                        name="selectedCountries"
                        multiple
                        value={formData.selectedCountries}
                        label="Countries"
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            selectedCountries: event.target.value
                          });
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                              '& .MuiMenuItem-root': {
                                minHeight: 'auto',
                                px: 1
                              }
                            }
                          }
                        }}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((countryCode) => {
                              const country = COUNTRIES.find(c => c.code === countryCode);
                              return (
                                <Chip 
                                  key={countryCode} 
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <img
                                        src={`/images/country/${countryCode.toLowerCase()}.svg`}
                                        alt={country?.name || countryCode}
                                        style={{ width: 14, height: 11, borderRadius: '1px' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                      {country?.name || countryCode}
                                    </Box>
                                  }
                                  size="small" 
                                />
                              );
                            })}
                          </Box>
                        )}
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          minWidth: "220px"
                        }}
                      >
                        {COUNTRIES.map(country => {
                          const isSelected = formData.selectedCountries.includes(country.code);
                          return (
                            <MenuItem 
                              key={country.code} 
                              value={country.code}
                              sx={{
                                backgroundColor: isSelected ? '#2e7d32 !important' : 'transparent',
                                color: isSelected ? 'white !important' : 'inherit',
                                '&:hover': {
                                  backgroundColor: isSelected ? '#1b5e20 !important' : 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img
                                  src={`/images/country/${country.code.toLowerCase()}.svg`}
                                  alt={country.name}
                                  style={{ width: 20, height: 15, borderRadius: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                {country.name}
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Unit Type</InputLabel>
                      <Select
                        name="unitType"
                        value={formData.unitType}
                        label="Unit Type"
                        onChange={handleChange}
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          minWidth: "220px"
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a unit type</em>
                        </MenuItem>
                        <MenuItem value="gram">gram</MenuItem>
                        <MenuItem value="ml">ml</MenuItem>
                        <MenuItem value="piece_portion">piece/portion</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Unit Value"
                      name="unitValue"
                      value={formData.unitValue}
                      onChange={handleChange}
                      placeholder="100"
                      helperText="Serving size amount"
                      error={!!errors.unitValue}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      name="description"
                      value={formData.languageSpec?.[currentLanguage]?.description || formData.description}
                      onChange={handleChange}
                      placeholder="Enter a detailed description of this food item..."
                      helperText="This will be translated to other languages when using AI"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TagSelector
                      selectedTagIds={selectedTagIds}
                      onTagsChange={setSelectedTagIds}
                      label="Select tags for this food"
                      placeholder="Choose relevant tags to categorize this food..."
                      currentLanguage={currentLanguage}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.showOriginalAmount || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, showOriginalAmount: e.target.checked }))}
                          color="primary"
                        />
                      }
                      label="Show Original Amount (bypass carb preference scaling)"
                      sx={{ 
                        mt: 1,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                          color: 'text.secondary'
                        }
                      }}
                    />
                  </Grid>

                </Grid>
              </Paper>

              {/* Image Upload Section */}
              <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                  📷 Image Settings
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      name="image"
                      value={formData.image}
                      onChange={handleChange}
                      placeholder="https://example.com/image.jpg"
                      helperText="Paste URL or use upload button below"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />

                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      disabled={formData.imageUploading}
                      sx={{
                        height: '56px',
                        borderRadius: 2,
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        '&:hover': {
                          borderStyle: 'dashed',
                          borderWidth: 2,
                        }
                      }}
                    >
                      {formData.imageUploading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <AddIcon sx={{ mr: 1 }} />
                          Upload Food Image
                        </>
                      )}
                      <input
                        type="file"
                        hidden
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        onChange={async (e) => {
                          console.log("Image upload triggered:", e.target.files);
                          const file = e.target.files[0];
                          if (!file) {
                            console.log("No file selected");
                            return;
                          }

                          console.log("File selected:", file.name, file.size, file.type);
                          setFormData(prev => ({ ...prev, imageUploading: true }));

                          try {
                            console.log("Validating image file...");
                            validateImageFile(file);
                            console.log("Processing image file...");
                            const processedFile = await processImageFile(file);
                            console.log("Processed file:", processedFile.name, processedFile.size);

                            const formDataObj = new FormData();
                            formDataObj.append("image", processedFile);
                            console.log("Uploading to API...");

                            const response = await api.post('/upload-image', formDataObj, {
                              headers: {
                                'Content-Type': 'multipart/form-data'
                              }
                            });

                            console.log("API response:", response.data);
                            if (response.data && response.data.success && response.data.url) {
                              handleChange({ target: { name: 'image', value: response.data.url } });
                              setNotification({
                                open: true,
                                message: `Image uploaded successfully! (${Math.round(processedFile.size / 1024)}KB, 200x200px)`,
                                severity: "success"
                              });
                            } else {
                              throw new Error('Invalid response from server');
                            }
                          } catch (error) {
                            console.error("Error uploading image:", error);
                            setNotification({
                              open: true,
                              message: error.message || "Failed to upload image",
                              severity: "error"
                            });
                          } finally {
                            setFormData(prev => ({ ...prev, imageUploading: false }));
                          }
                        }}
                      />
                    </Button>
                  </Grid>

                  {/* Remove Image Button */}
                  {formData.image && (
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        disabled={formData.imageUploading}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, image: '' }));
                          setNotification({
                            open: true,
                            message: "Image removed successfully",
                            severity: "success"
                          });
                        }}
                        sx={{
                          height: '56px',
                          borderRadius: 2,
                        }}
                      >
                        <DeleteIcon sx={{ mr: 1 }} />
                        Remove Image
                      </Button>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <Box sx={{
                      p: 3,
                      backgroundColor: '#f8fafc',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0'
                    }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={handleGenerateImage}
                        disabled={imageGenerating || !formData.name}
                        fullWidth
                        sx={{
                          height: '48px',
                          fontWeight: 'bold',
                          mb: 2
                        }}
                      >
                        {imageGenerating ? "🧠 Generating..." : "🧠 Generate AI Image"}
                      </Button>

                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        AI will create a realistic food image with plain background
                      </Typography>

                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                        <strong>Note:</strong> Images are automatically resized to 200x200px and compressed to &lt;100KB
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Nutritional Information Section */}
              <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                      🥗 Nutritional Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enter nutritional values per {formData.unitValue || "100"}{formData.unitType || "g"} serving
                    </Typography>
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={handleFillNutrition}
                      disabled={nutritionLoading || !formData.name}
                      sx={{
                        height: '48px',
                        fontWeight: 'bold',
                        minWidth: '180px'
                      }}
                    >
                      {nutritionLoading ? "🧠 Analyzing..." : "🧠 Fill with AI"}
                    </Button>
                    {showLongLoadingMessage && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          display: 'block', 
                          textAlign: 'center', 
                          mt: 1,
                          fontStyle: 'italic'
                        }}
                      >
                        ⏳ This might take a minute as we analyze the food details...
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Calories"
                      name="calories"
                      value={formData.calories}
                      onChange={handleChange}
                      inputProps={{ step: "0.01", min: "0" }}
                      error={!!errors.calories}
                      helperText={errors.calories}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Protein (g)"
                      name="protein"
                      value={formData.protein}
                      onChange={handleChange}
                      inputProps={{ step: "0.01", min: "0" }}
                      error={!!errors.protein}
                      helperText={errors.protein}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Fat (g)"
                      name="fat"
                      value={formData.fat}
                      onChange={handleChange}
                      inputProps={{ step: "0.01", min: "0" }}
                      error={!!errors.fat}
                      helperText={errors.fat}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Carbs (g)"
                      name="carbs"
                      value={formData.carbs}
                      onChange={handleChange}
                      inputProps={{ step: "0.01", min: "0" }}
                      error={!!errors.carbs}
                      helperText={errors.carbs}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Glycemic Index"
                      name="glycemicIndex"
                      value={formData.glycemicIndex}
                      onChange={handleChange}
                      inputProps={{ step: "1", min: "0", max: "100" }}
                      helperText="Scale of 0-100 (blood sugar impact)"
                      error={!!errors.glycemicIndex}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="NOVA Group"
                      name="novaGroup"
                      value={formData.novaGroup}
                      onChange={handleChange}
                      helperText={formData.novaSource
                        ? `Current value from: ${NOVA_SOURCE_LABELS[formData.novaSource] || formData.novaSource}. A changed value is saved as expert.`
                        : 'Degree of processing (Nova-Score in the app)'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    >
                      <MenuItem value=""><em>Not set</em></MenuItem>
                      {NOVA_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>

              {/* Language-Specific Information Section */}
              <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                  🌍 Language-Specific Information
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  AI automatically translates food names and descriptions to all languages. You can customize or upload language-specific images.
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                  <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTab-root': {
                        minHeight: '48px',
                        textTransform: 'none',
                        fontWeight: 500
                      }
                    }}
                  >
                    {Object.keys(formData.languageSpec).map((lang) => (
                      <Tab
                        key={lang}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LanguageIcon fontSize="small" />
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                            {!defaultLanguages.includes(lang) && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveLanguage(lang);
                                }}
                                sx={{ ml: 1 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        }
                      />
                    ))}
                    <Tab
                      icon={<AddIcon />}
                      label="Add Language"
                      sx={{ minWidth: '140px' }}
                    />
                  </Tabs>
                </Box>

                {Object.keys(formData.languageSpec).map((lang, index) => (
                  <Box
                    key={lang}
                    role="tabpanel"
                    hidden={currentTab !== index}
                    sx={{ py: 2 }}
                  >
                    {currentTab === index && (
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={`Food Name in ${lang.charAt(0).toUpperCase() + lang.slice(1)}`}
                            value={formData.languageSpec[lang]?.name || ""}
                            onChange={(e) =>
                              handleLanguageChange(lang, "name", e.target.value)
                            }
                            placeholder={`Enter the food name in ${lang}`}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover fieldset': {
                                  borderColor: 'secondary.main',
                                },
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label={`Description in ${lang.charAt(0).toUpperCase() + lang.slice(1)}`}
                            value={formData.languageSpec[lang]?.description || ""}
                            onChange={(e) =>
                              handleLanguageChange(lang, "description", e.target.value)
                            }
                            placeholder={`Enter the food description in ${lang}`}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover fieldset': {
                                  borderColor: 'secondary.main',
                                },
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{
                              height: "56px",
                              borderRadius: 2,
                              borderColor: 'secondary.main',
                              color: 'secondary.main',
                              '&:hover': {
                                borderColor: 'secondary.dark',
                                backgroundColor: 'secondary.light'
                              }
                            }}
                            disabled={formData.languageSpec[lang]?.uploading}
                          >
                            {formData.languageSpec[lang]?.uploading ? (
                              <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <AddIcon sx={{ mr: 1 }} />
                                Upload {lang.charAt(0).toUpperCase() + lang.slice(1)} Image
                              </>
                            )}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => handleLanguageImageUpload(lang, e)}
                            />
                          </Button>
                        </Grid>

                        {/* Remove Language Image Button */}
                        {formData.languageSpec[lang]?.customImageUrl && (
                          <Grid item xs={12} md={6}>
                            <Button
                              variant="outlined"
                              color="error"
                              fullWidth
                              disabled={formData.languageSpec[lang]?.uploading}
                              onClick={() => {
                                handleLanguageChange(lang, 'customImageUrl', '');
                                setNotification({
                                  open: true,
                                  message: `${lang.charAt(0).toUpperCase() + lang.slice(1)} image removed successfully`,
                                  severity: "success"
                                });
                              }}
                              sx={{
                                height: '56px',
                                borderRadius: 2,
                              }}
                            >
                              <DeleteIcon sx={{ mr: 1 }} />
                              Remove {lang.charAt(0).toUpperCase() + lang.slice(1)} Image
                            </Button>
                          </Grid>
                        )}

                        {formData.languageSpec[lang]?.customImageUrl && (
                          <Grid item xs={12}>
                            <Box sx={{
                              border: '1px solid #e2e8f0',
                              borderRadius: 2,
                              overflow: 'hidden',
                              maxWidth: '200px'
                            }}>
                              <Typography variant="caption" sx={{
                                p: 1,
                                backgroundColor: '#f8fafc',
                                display: 'block',
                                textAlign: 'center',
                                fontWeight: 'bold'
                              }}>
                                {lang.charAt(0).toUpperCase() + lang.slice(1)} Image
                              </Typography>
                              <Box sx={{ p: 1 }}>
                                <img
                                  src={formData.languageSpec[lang].customImageUrl}
                                  alt={`${lang} preview`}
                                  style={{
                                    width: "100%",
                                    maxHeight: '100px',
                                    objectFit: 'contain',
                                    borderRadius: "4px"
                                  }}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Box>
                ))}
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Add Language Dialog */}
      <Dialog
        open={newLanguageDialog}
        onClose={() => setNewLanguageDialog(false)}
      >
        <DialogTitle>Add New Language</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Language Name"
            fullWidth
            value={newLanguageName}
            onChange={(e) => setNewLanguageName(e.target.value)}
            helperText="Example: Spanish, Italian, Portuguese, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewLanguageDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddLanguage}
            variant="contained"
            color="primary"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
          iconMapping={{
            success: <CheckCircleIcon fontSize="inherit" />,
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Bulk Insert Component
function BulkInsertForm({ onBack }) {
  const [bulkData, setBulkData] = useState('');
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const processBulkData = async () => {
    if (!bulkData.trim()) {
      setNotification({
        open: true,
        message: 'Please enter food data to process',
        severity: 'error'
      });
      return;
    }

    setProcessing(true);
    const lines = bulkData.trim().split('\n');
    const foods = [];
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        try {
          // Expected format: name, category, calories, protein, fat, carbs, glycemicIndex, cuisineType, countries (semicolon-separated)
          const parts = line.split(',').map(part => part.trim());

          if (parts.length < 2) {
            errors.push(`Line ${i + 1}: Invalid format (minimum: name, category)`);
            errorCount++;
            continue;
          }

          // Parse countries from semicolon-separated list
          const countriesString = parts[8] || '';
          const countries = countriesString ? 
            countriesString.split(';').map(c => c.trim()).filter(c => c) : [];

          const foodData = {
            name: parts[0],
            category: parts[1],
            calories: parts[2] ? parseFloat(parts[2]) : null,
            protein: parts[3] ? parseFloat(parts[3]) : null,
            fat: parts[4] ? parseFloat(parts[4]) : null,
            carbs: parts[5] ? parseFloat(parts[5]) : null,
            glycemicIndex: parts[6] ? parseFloat(parts[6]) : null,
            cuisineType: parts[7] || null,
            countries: countries
          };

          // Call API to create food
          const response = await api.post('/foods', foodData);
          if (response.data.success) {
            successCount++;
            foods.push(foodData);
          } else {
            const errorMsg = response.data?.error?.userMessage || 
                            response.data?.error?.message || 
                            response.data?.error || 
                            'Failed to create';
            errors.push(`Line ${i + 1}: ${errorMsg}`);
            errorCount++;
          }
        } catch (error) {
          errors.push(`Line ${i + 1}: ${error.message}`);
          errorCount++;
        }
      }

      setResults({
        total: lines.filter(line => line.trim()).length,
        success: successCount,
        errors: errorCount,
        errorDetails: errors,
        foods
      });

      if (successCount > 0) {
        setNotification({
          open: true,
          message: `Successfully created ${successCount} food items${errorCount > 0 ? ` (${errorCount} failed)` : ''}`,
          severity: errorCount > 0 ? 'warning' : 'success'
        });
      } else {
        setNotification({
          open: true,
          message: 'No food items were created. Please check your data format.',
          severity: 'error'
        });
      }

    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to process bulk data',
        severity: 'error'
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100vw', px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <IconButton edge="start" onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" color="primary.main">
          Bulk Food Insertion
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          📝 Data Format Instructions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter food data in CSV format, one food per line. Format: <strong>name, category, calories, protein, fat, carbs, glycemicIndex</strong>
        </Typography>

        <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" fontFamily="monospace">
            Example:<br />
            Apple, Fruits, 52, 0.3, 0.2, 14, 36<br />
            Chicken Breast, Meat, 165, 31, 3.6, 0, 0<br />
            Brown Rice, Grains, 111, 2.6, 0.9, 23, 50
          </Typography>
        </Box>

        <TextField
          multiline
          rows={12}
          fullWidth
          label="Food Data (CSV Format)"
          value={bulkData}
          onChange={(e) => setBulkData(e.target.value)}
          placeholder="Enter food data here, one per line..."
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            onClick={processBulkData}
            disabled={processing || !bulkData.trim()}
            startIcon={processing ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {processing ? 'Processing...' : 'Process Bulk Data'}
          </Button>
          <Button variant="outlined" onClick={() => setBulkData('')}>
            Clear
          </Button>
        </Box>

        {results && (
          <Paper elevation={2} sx={{ p: 2, backgroundColor: 'success.light' }}>
            <Typography variant="h6" gutterBottom>
              📈 Processing Results
            </Typography>
            <Typography variant="body1">
              Total items processed: <strong>{results.total}</strong>
            </Typography>
            <Typography variant="body1" color="success.main">
              Successfully created: <strong>{results.success}</strong>
            </Typography>
            {results.errors > 0 && (
              <>
                <Typography variant="body1" color="error.main">
                  Failed: <strong>{results.errors}</strong>
                </Typography>
                <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="subtitle2" gutterBottom>Error Details:</Typography>
                  {results.errorDetails.map((error, index) => (
                    <Typography key={index} variant="body2" color="error.main" sx={{ fontFamily: 'monospace' }}>
                      {error}
                    </Typography>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        )}
      </Paper>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Main FoodManager component that switches between list, form, and bulk insert
export default function FoodManager() {
  const navigate = useNavigate();
  const [view, setView] = useState('list');
  const [selectedFoodId, setSelectedFoodId] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);

  // Check for URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const editId = urlParams.get('edit');

    if (editId) {
      setSelectedFoodId(editId);
      setView('form');
      // Clean up URL
      navigate('/food-manager', { replace: true });
    }
  }, [navigate]);

  const handleEditFood = (id) => {
    if (id === 'bulk-insert') {
      setView('bulk-insert');
    } else {
      setSelectedFoodId(id);
      setView('form');
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedFoodId(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  if (view === 'list') {
    return <FoodList onEdit={handleEditFood} onBack={handleNavigation} currentLanguage={currentLanguage} setCurrentLanguage={setCurrentLanguage} />;
  } else if (view === 'bulk-insert') {
    return <BulkInsertForm onBack={handleBackToList} />;
  } else {
    return <FoodForm foodId={selectedFoodId} onBack={handleBackToList} currentLanguage={currentLanguage} />;
  }
}